import React, { useEffect, useState, useCallback } from 'react';
import { personalPorRatio } from '../utils/personalRatio';
import { menusAPI, platosAPI, alergenosAPI, materialesAPI, plantillasAPI, ubicacionesAPI, proveedoresAPI, getAuthToken } from '../api/api';
import { Plus, Search, Edit, Trash2, X, UtensilsCrossed, DollarSign, Users, Clock, Package, ShoppingCart, Layers, Copy, CheckCircle, FileText, RefreshCw, ChevronDown, Loader2, Download, Settings, Eye, EyeOff } from 'lucide-react';
import AlergenoIcon from '../components/AlergenoIcon';
import LoadingOverlay from '../components/LoadingOverlay';
import MenuEstadisticas from '../components/MenuEstadisticas';

// Función para normalizar texto (quitar acentos) para búsquedas
const normalizeText = (text) => {
  if (!text) return '';
  return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

// Componente de selector de material con autocompletado
const MaterialSelectRow = ({ item, materiales, onSelect, onCreateNew, onDelete, cantidadValue, onCantidadChange, cantidadLabel }) => {
  const [searchTerm, setSearchTerm] = useState(item.nombre || '');
  const [isOpen, setIsOpen] = useState(false);
  const [focused, setFocused] = useState(false);

  const filteredMateriales = [...new Map(materiales.map(mat => [mat.nombre, mat])).values()]
    .filter(mat => !searchTerm || normalizeText(mat.nombre).includes(normalizeText(searchTerm)))
    .sort((a, b) => a.nombre.localeCompare(b.nombre))
    .slice(0, 15);

  const handleSelect = (nombre) => {
    setSearchTerm(nombre);
    onSelect(nombre);
    setIsOpen(false);
  };

  return (
    <div className="flex gap-2 mb-2 items-center bg-gray-50 p-2 rounded">
      <div className="flex-1 relative">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => { setFocused(true); setIsOpen(true); }}
          onBlur={() => setTimeout(() => { setFocused(false); setIsOpen(false); }, 200)}
          placeholder="🔍 Buscar o escribir material..."
          className="w-full border rounded px-3 py-1.5 text-sm pr-8"
        />
        <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
        
        {isOpen && (focused || searchTerm) && (
          <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
            {filteredMateriales.map(mat => (
              <div
                key={mat.id}
                onClick={() => handleSelect(mat.nombre)}
                className="px-3 py-2 hover:bg-purple-50 cursor-pointer text-sm"
              >
                {mat.nombre}
              </div>
            ))}
            <div
              onClick={() => { onCreateNew(); setIsOpen(false); }}
              className="px-3 py-2 hover:bg-green-50 cursor-pointer text-sm text-green-700 font-medium border-t bg-green-50/50"
            >
              ➕ Crear nuevo material
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        <input
          type="number"
          min="1"
          value={cantidadValue}
          onChange={(e) => onCantidadChange(e.target.value)}
          className="w-16 border rounded px-2 py-1 text-sm text-center"
        />
        <span className="text-xs text-gray-500 whitespace-nowrap">{cantidadLabel}</span>
      </div>
      <button type="button" onClick={onDelete} className="text-red-600 hover:text-red-800">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

// Componente de selector de material por persona con autocompletado
const MaterialSelectRowPersona = ({ item, materiales, onSelect, onCreateNew, onDelete, cantidadValue, onCantidadChange, personasValue, onPersonasChange }) => {
  const [searchTerm, setSearchTerm] = useState(item.nombre || '');
  const [isOpen, setIsOpen] = useState(false);
  const [focused, setFocused] = useState(false);

  const filteredMateriales = [...new Map(materiales.map(mat => [mat.nombre, mat])).values()]
    .filter(mat => !searchTerm || normalizeText(mat.nombre).includes(normalizeText(searchTerm)))
    .sort((a, b) => a.nombre.localeCompare(b.nombre))
    .slice(0, 15);

  const handleSelect = (nombre) => {
    setSearchTerm(nombre);
    onSelect(nombre);
    setIsOpen(false);
  };

  return (
    <div className="flex gap-2 mb-2 items-center bg-gray-50 p-2 rounded">
      <div className="flex-1 relative">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => { setFocused(true); setIsOpen(true); }}
          onBlur={() => setTimeout(() => { setFocused(false); setIsOpen(false); }, 200)}
          placeholder="🔍 Buscar o escribir material..."
          className="w-full border rounded px-3 py-1.5 text-sm pr-8"
        />
        <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
        
        {isOpen && (focused || searchTerm) && (
          <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
            {filteredMateriales.map(mat => (
              <div
                key={mat.id}
                onClick={() => handleSelect(mat.nombre)}
                className="px-3 py-2 hover:bg-purple-50 cursor-pointer text-sm"
              >
                {mat.nombre}
              </div>
            ))}
            <div
              onClick={() => { onCreateNew(); setIsOpen(false); }}
              className="px-3 py-2 hover:bg-green-50 cursor-pointer text-sm text-green-700 font-medium border-t bg-green-50/50"
            >
              ➕ Crear nuevo material
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        <input
          type="number"
          min="1"
          value={cantidadValue}
          onChange={(e) => onCantidadChange(e.target.value)}
          className="w-12 border rounded px-2 py-1 text-sm text-center"
        />
        <span className="text-xs text-gray-500">cada</span>
        <input
          type="number"
          min="1"
          value={personasValue}
          onChange={(e) => onPersonasChange(e.target.value)}
          className="w-12 border rounded px-2 py-1 text-sm text-center"
        />
        <span className="text-xs text-gray-500">pax</span>
      </div>
      <button type="button" onClick={onDelete} className="text-red-600 hover:text-red-800">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

// Helper para agrupar material por bloques (plantillas)
const agruparMaterialPorBloques = (materiales) => {
  if (!materiales || !Array.isArray(materiales)) return [];
  
  const grupos = {};
  materiales.forEach((item, originalIndex) => {
    const bloque = item.bloque || 'Manual';
    if (!grupos[bloque]) {
      grupos[bloque] = [];
    }
    grupos[bloque].push({ ...item, originalIndex });
  });
  
  // Ordenar: primero los bloques de plantillas (alfabéticamente), luego "Manual" al final
  return Object.entries(grupos)
    .sort(([a], [b]) => {
      if (a === 'Manual') return 1;
      if (b === 'Manual') return -1;
      return a.localeCompare(b);
    })
    .map(([bloque, items]) => ({ bloque, items }));
};

const Menus = () => {
  const [menus, setMenus] = useState([]);
  const [platos, setPlatos] = useState([]);
  const [alergenos, setAlergenos] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('menu'); // 'menu' o 'servicio'
  const [filtroEstado, setFiltroEstado] = useState('activo'); // 'todos', 'activo', 'inactivo'
  const [filtroRevisado, setFiltroRevisado] = useState('todos'); // 'todos', 'revisado', 'sin_revisar'
  const [showModal, setShowModal] = useState(false);
  const [showPlantillaModal, setShowPlantillaModal] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [editingPlantilla, setEditingPlantilla] = useState(null);
  const [activeTab, setActiveTab] = useState('general'); // general, material, personal, tiempos, costes
  const [viewMode, setViewMode] = useState('menus'); // 'menus' or 'plantillas'
  
  const [formData, setFormData] = useState({
    nombre: '',
    tipo: 'menu', // 'menu' o 'servicio'
    tipo_menu: '',
    formato_servicio: 'autoservicio',
    platos_ids: [],
    unidades_por_plato: {}, // { plato_id: cantidad_por_persona }
    
    // Filtros de alérgenos y características
    alergenos_evitar: [],
    caracteristicas_evitar: [], // contiene_carne, derivado_animal, contiene_cerdo, no_apto_embarazadas, sin_lactosa
    precio_maximo: null, // Filtro de precio máximo
    
    // Tiempos
    tiempo_montaje_min: 0,
    duracion_evento_min: 0,
    tiempo_desmontaje_min: 0,
    
    // Material
    material_fijo: [],
    material_por_persona: [],
    plantilla_menaje_id: null,
    
    // Personal - Nueva lógica
    personal_montaje: 1, // Campo requerido por backend
    ratio_camareros: 0, // 1 camarero cada X personas (0 = no hay camareros)
    ratio_extras: 0, // 1 extra cada X personas (0 = no hay extras)
    horas_por_camarero: 4,
    coste_camarero_hora: 25.0, // Campo requerido por backend
    camareros_necesarios: 0, // Campo requerido por backend
    
    // Textiles
    textiles: [],
    coste_lavanderia_total: 0,
    
    // Notas
    instrucciones_montaje: '',
    requisitos_lugar: '',
    requisitos_montaje: '',
    requiere_horario: null,
    notas_especiales: '',
        
    // Costes (calculados por backend pero necesarios en el modelo)
    coste_ingredientes: 0,
    coste_personal_cocina: 0,
    coste_personal_evento: 0,
    coste_transporte: 0,
    coste_total: 0,
    precio_total: 0,
    
    // Alérgenos e imagen
    alergenos_ids: [],
    foto_portada: null,
    activo: true, // Por defecto, los menús nuevos están activos
    
    // Precios
    iva: 10, // Ahora en números enteros
    margen: 40, // Ahora en números enteros
    precio_venta: 0,
    
    // Bebidas incluidas
    bebida_incluida: false,
    bebidas_por_persona: 1,

    // "Es alquiler": lo trae el proveedor al evento (no se carga en la nave)
    es_alquiler: false
  });

  // Estados para filtros de platos
  const [busquedaPlato, setBusquedaPlato] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('todos');
  const [mostrarFiltrosAvanzados, setMostrarFiltrosAvanzados] = useState(false);
  const [mostrarAnadirPlatos, setMostrarAnadirPlatos] = useState(false);

  const [materiales, setMateriales] = useState([]);
  const [showCreateMaterialModal, setShowCreateMaterialModal] = useState(false);
  const [newMaterial, setNewMaterial] = useState({ nombre: '', tipo: 'desechable' });

  // Estado para modal de catálogo PDF
  const [showCatalogoModal, setShowCatalogoModal] = useState(false);
  const [catalogoConfig, setCatalogoConfig] = useState({
    menu_ids: [],
    excluir_servicios: true,
    servicios_incluir: [],
    mostrar_precio: true,
    mostrar_alergenos: true,
    modo_alergenos: 'iconos',
    mostrar_tiempos: false,
    mostrar_personal: false,
    subtitulo: 'Servicios de Catering'
  });
  const [generandoPdf, setGenerandoPdf] = useState(false);

  const [plantillaFormData, setPlantillaFormData] = useState({
    nombre: '',
    descripcion: '',
    material_fijo: [],
    material_por_persona: [],
    textiles: [],
    servicios: [],  // NUEVO: servicios (platos tipo servicio)
    activo: true
  });

  // Estados para el modal completo de crear material
  const [ubicaciones, setUbicaciones] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [nuevoMaterialFormData, setNuevoMaterialFormData] = useState({
    nombre: '',
    categoria: 'menaje',
    tipo_coste: 'inventariable',
    formato_unidad: 'unidades',
    cantidad_por_envase: 1,
    precio_por_envase: 0,
    precio_unitario: 0,
    precio_compra: 0,
    stock: 0,
    stock_minimo: 0,
    ubicacion: '',
    proveedor_id: null,
    notas: '',
    activo: true
  });
  const [showUbicacionModal, setShowUbicacionModal] = useState(false);
  const [nuevaUbicacion, setNuevaUbicacion] = useState('');

  // Función para recargar solo materiales (sin cerrar modal)
  const reloadMateriales = async () => {
    try {
      const materialesRes = await materialesAPI.getAll();
      setMateriales(Array.isArray(materialesRes.data) ? materialesRes.data.filter(m => m.activo) : []);
    } catch (error) {
      console.error('Error reloading materiales:', error);
    }
  };

  // Cargar ubicaciones y proveedores
  const fetchUbicacionesProveedores = async () => {
    try {
      const [ubicacionesRes, proveedoresRes] = await Promise.all([
        ubicacionesAPI.getAll(true),
        proveedoresAPI.getAll()
      ]);
      setUbicaciones(ubicacionesRes.data || []);
      setProveedores(proveedoresRes.data || []);
    } catch (error) {
      console.error('Error fetching ubicaciones/proveedores:', error);
    }
  };

  // Manejar cambio de precio/cantidad en consumibles
  const handleEnvaseChange = (field, value) => {
    const newData = { ...nuevoMaterialFormData, [field]: value };
    
    // Recalcular precio unitario si cambia cantidad o precio del envase
    if (field === 'precio_por_envase' || field === 'cantidad_por_envase') {
      const precio = field === 'precio_por_envase' ? value : nuevoMaterialFormData.precio_por_envase;
      const cantidad = field === 'cantidad_por_envase' ? value : nuevoMaterialFormData.cantidad_por_envase;
      if (cantidad > 0) {
        newData.precio_unitario = precio / cantidad;
      }
    }
    
    setNuevoMaterialFormData(newData);
  };

  // Crear nueva ubicación
  const handleCreateUbicacion = async () => {
    if (!nuevaUbicacion.trim()) return;
    try {
      await ubicacionesAPI.create({ nombre: nuevaUbicacion.trim() });
      await fetchUbicacionesProveedores();
      setNuevoMaterialFormData({ ...nuevoMaterialFormData, ubicacion: nuevaUbicacion.trim() });
      setShowUbicacionModal(false);
      setNuevaUbicacion('');
    } catch (error) {
      console.error('Error creating ubicacion:', error);
      alert('Error al crear la ubicación');
    }
  };

  // Guardar nuevo material
  const handleSaveNuevoMaterial = async () => {
    if (!nuevoMaterialFormData.nombre?.trim()) {
      alert('Por favor, introduce un nombre para el material');
      return;
    }
    
    try {
      await materialesAPI.create(nuevoMaterialFormData);
      await reloadMateriales();
      setShowCreateMaterialModal(false);
      
      // Reset form
      setNuevoMaterialFormData({
        nombre: '',
        categoria: 'menaje',
        tipo_coste: 'inventariable',
        formato_unidad: 'unidades',
        cantidad_por_envase: 1,
        precio_por_envase: 0,
        precio_unitario: 0,
        precio_compra: 0,
        stock: 0,
        stock_minimo: 0,
        ubicacion: '',
        proveedor_id: null,
        notas: '',
        activo: true
      });
      
      alert('Material creado correctamente');
    } catch (error) {
      console.error('Error creating material:', error);
      alert('Error al crear el material');
    }
  };

  useEffect(() => {
    fetchData();
    fetchUbicacionesProveedores();
  }, []);

  const fetchData = async () => {
    try {
      const [menusRes, platosRes, alergenosRes, plantillasRes, materialesRes] = await Promise.all([
        menusAPI.getAll(),
        platosAPI.getAll(),
        alergenosAPI.getAll(),
        plantillasAPI.getAll(),
        materialesAPI.getAll()
      ]);
      setMenus(menusRes.data);
      setPlatos(platosRes.data.filter(p => p.activo));
      setAlergenos(alergenosRes.data);
      setPlantillas(Array.isArray(plantillasRes.data) ? plantillasRes.data : []);
      setMateriales(Array.isArray(materialesRes.data) ? materialesRes.data.filter(m => m.activo) : []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este menú?')) {
      try {
        await menusAPI.delete(id);
        fetchData();
      } catch (error) {
        console.error('Error deleting menu:', error);
      }
    }
  };

  const handleToggleRevisado = async (id) => {
    try {
      const result = await menusAPI.toggleRevisado(id);
      // Update local state
      setMenus(prevMenus => prevMenus.map(menu => 
        menu.id === id ? { ...menu, revisado: result.data.revisado } : menu
      ));
    } catch (error) {
      console.error('Error toggling revisado:', error);
    }
  };

  const handleToggleActivo = async (id, event) => {
    // Prevenir que el click en el toggle abra el modal de edición
    event.stopPropagation();
    try {
      const result = await menusAPI.toggleActivo(id);
      // Update local state
      setMenus(prevMenus => prevMenus.map(menu => 
        menu.id === id ? { ...menu, activo: result.data.activo } : menu
      ));
    } catch (error) {
      console.error('Error toggling activo:', error);
    }
  };


  // Generar catálogo PDF con configuración
  const generarCatalogoPdf = async () => {
    setGenerandoPdf(true);
    try {
      const token = getAuthToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/menus/generar-catalogo-pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(catalogoConfig)
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Catalogo_Menus_Duetto_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setShowCatalogoModal(false);
      } else {
        const err = await response.json();
        alert(`Error: ${err.detail || 'Error al generar catálogo'}`);
      }
    } catch (error) {
      console.error('Error generating catalog:', error);
      alert('Error al generar el catálogo PDF');
    } finally {
      setGenerandoPdf(false);
    }
  };



  const openModal = async (menu = null) => {
    // Refrescar datos de platos para tener precios actualizados (ej: Coca-Cola para cálculo de bebidas)
    try {
      const platosRes = await platosAPI.getAll();
      setPlatos(platosRes.data.filter(p => p.activo));
    } catch (error) {
      console.error('Error refreshing platos data:', error);
    }
    
    if (menu) {
      setEditingMenu(menu);
      
      // Parse menu.platos array into platos_ids and unidades_por_plato
      const platosIds = [];
      const unidadesPorPlato = {};
      
      if (menu.platos && Array.isArray(menu.platos)) {
        menu.platos.forEach(platoRef => {
          if (platoRef.plato_id) {
            platosIds.push(platoRef.plato_id);
            unidadesPorPlato[platoRef.plato_id] = platoRef.cantidad || 1;
          }
        });
      }
      
      // Normalizar tipo_menu a valores válidos del enum
      const normalizeTipoMenu = (tipo) => {
        if (!tipo) return '';
        const tipoLower = tipo.toLowerCase();
        const mapping = {
          'cóctel': 'coctel',
          'coctel': 'coctel',
          'desayuno / coffee': 'desayuno',
          'desayuno': 'desayuno',
          'lunch': 'lunch',
          'vino español': 'vino_espanol',
          'vino_espanol': 'vino_espanol',
          'cena': 'cena',
          'otros': 'otros',
          'suplemento comida': 'otros',
          'suplemento bebida': 'otros'
        };
        return mapping[tipoLower] || '';
      };
      
      setFormData({
        nombre: menu.nombre || '',
        tipo: menu.tipo || 'menu', // 'menu' o 'servicio'
        tipo_menu: normalizeTipoMenu(menu.tipo_menu),
        formato_servicio: menu.formato_servicio || 'autoservicio',
        platos_ids: platosIds,
        unidades_por_plato: unidadesPorPlato,
        alergenos_evitar: menu.alergenos_evitar || [],
        caracteristicas_evitar: menu.caracteristicas_evitar || [],
        precio_maximo: menu.precio_maximo || null,
        tiempo_montaje_min: menu.tiempo_montaje_min || 60,
        duracion_evento_min: menu.duracion_evento_min || 60,
        tiempo_desmontaje_min: menu.tiempo_desmontaje_min || 60,
        material_fijo: menu.material_fijo || [],
        material_por_persona: menu.material_por_persona || [],
        plantilla_menaje_id: menu.plantilla_menaje_id || null,
        personal_montaje: menu.personal_montaje || 1,
        ratio_camareros: menu.ratio_camareros !== undefined && menu.ratio_camareros !== null ? menu.ratio_camareros : 20,
        ratio_extras: menu.ratio_extras !== undefined && menu.ratio_extras !== null ? menu.ratio_extras : 100,
        horas_por_camarero: menu.horas_por_camarero !== undefined && menu.horas_por_camarero !== null ? menu.horas_por_camarero : 4,
        coste_camarero_hora: menu.coste_camarero_hora || 25.0,
        camareros_necesarios: menu.camareros_necesarios || 0,
        textiles: menu.textiles || [],
        coste_lavanderia_total: menu.coste_lavanderia_total || 0,
        instrucciones_montaje: menu.instrucciones_montaje || '',
        requisitos_lugar: menu.requisitos_lugar || '',
        requisitos_montaje: menu.requisitos_montaje || '',
        requiere_horario: typeof menu.requiere_horario === 'boolean' ? menu.requiere_horario : null,
        notas_especiales: menu.notas_especiales || '',
        coste_ingredientes: menu.coste_ingredientes || 0,
        coste_personal_cocina: menu.coste_personal_cocina || 0,
        coste_personal_evento: menu.coste_personal_evento || 0,
        coste_transporte: menu.coste_transporte || 0,
        coste_total: menu.coste_total || 0,
        precio_total: menu.precio_total || 0,
        alergenos_ids: menu.alergenos_ids || [],
        foto_portada: menu.foto_portada || null,
        iva: menu.iva !== undefined && menu.iva !== null ? (menu.iva < 1 ? menu.iva * 100 : menu.iva) : 10,
        margen: menu.margen !== undefined && menu.margen !== null ? (menu.margen < 1 ? menu.margen * 100 : menu.margen) : 40,
        precio_venta: menu.precio_venta !== undefined && menu.precio_venta !== null && menu.precio_venta > 0 
          ? menu.precio_venta 
          : (menu.precio_persona || 0),
        activo: menu.activo !== undefined ? menu.activo : true,
        bebida_incluida: menu.bebida_incluida || false,
        bebidas_por_persona: menu.bebidas_por_persona || 1,
        es_alquiler: !!menu.es_alquiler
      });
    } else {
      setEditingMenu(null);
      setFormData({
        nombre: '',
        tipo: 'menu', // 'menu' o 'servicio'
        tipo_menu: '',
        formato_servicio: 'autoservicio',
        platos_ids: [],
        unidades_por_plato: {},
        alergenos_evitar: [],
        caracteristicas_evitar: [],
        precio_maximo: null,
        tiempo_montaje_min: 0,
        duracion_evento_min: 0,
        tiempo_desmontaje_min: 0,
        material_fijo: [],
        material_por_persona: [],
        plantilla_menaje_id: null,
        personal_montaje: 1,
        ratio_camareros: 0,
        ratio_extras: 0,
        horas_por_camarero: 4,
        coste_camarero_hora: 25.0,
        camareros_necesarios: 0,
        textiles: [],
        coste_lavanderia_total: 0,
        instrucciones_montaje: '',
        requisitos_lugar: '',
        requisitos_montaje: '',
        requiere_horario: null,
        notas_especiales: '',
        coste_ingredientes: 0,
        coste_personal_cocina: 0,
        coste_personal_evento: 0,
        coste_transporte: 0,
        coste_total: 0,
        precio_total: 0,
        alergenos_ids: [],
        foto_portada: null,
        iva: 10,
        margen: 40,
        precio_venta: 0,
        activo: true,
        bebida_incluida: false,
        bebidas_por_persona: 1,
        es_alquiler: false
      });
    }
    setActiveTab('general');
    setShowModal(true);
  };

  const openPlantillaModal = (plantilla = null) => {
    if (plantilla) {
      setEditingPlantilla(plantilla);
      setPlantillaFormData({
        nombre: plantilla.nombre || '',
        descripcion: plantilla.descripcion || '',
        material_fijo: plantilla.material_fijo || [],
        material_por_persona: plantilla.material_por_persona || [],
        textiles: plantilla.textiles || [],
        servicios: plantilla.servicios || [],  // NUEVO: cargar servicios
        activo: plantilla.activo !== undefined ? plantilla.activo : true
      });
    } else {
      setEditingPlantilla(null);
      setPlantillaFormData({
        nombre: '',
        descripcion: '',
        material_fijo: [],
        material_por_persona: [],
        textiles: [],
        servicios: [],  // NUEVO
        activo: true
      });
    }
    setShowPlantillaModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      
      // Calcular costes antes de guardar
      const costePlatos = calcularCostePlatos() || 0;
      const materialesCostes = calcularCosteMateriales(40);
      const costoMaterialPP = parseFloat(materialesCostes.costoPorPersonaUnitario) || 0;
      const personalInfo = calcularCostePersonal(40);
      const costoPersonalPP = parseFloat(personalInfo.costePorPersona) || 0;
      
      // Coste total por persona (platos + material + personal)
      const costeTotalCalculado = costePlatos + costoMaterialPP + costoPersonalPP;
      
      // Convertir IVA y margen a decimales para el backend
      const dataToSave = {
        ...formData,
        iva: formData.iva / 100,
        tipo_iva: formData.iva, // Guardar también como entero para compatibilidad
        margen: formData.margen / 100,
        // Guardar los costes calculados
        coste_ingredientes: costePlatos,
        coste_personal_evento: personalInfo.costeTotalPersonal || 0,
        coste_total: costeTotalCalculado,
        coste_persona: costeTotalCalculado
      };
      
      
      if (editingMenu) {
        const response = await menusAPI.update(editingMenu.id, dataToSave);
      } else {
        const response = await menusAPI.create(dataToSave);
      }
      
      setShowModal(false);
      await fetchData();
    } catch (error) {
      console.error('Error saving menu:', error);
      console.error('Error details:', error.response?.data);
      
      // Format error message properly
      let errorMessage = 'Error al guardar el menú';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Pydantic validation errors
          errorMessage += ':\n' + error.response.data.detail.map(err => 
            `- ${err.loc?.join('.') || 'Campo'}: ${err.msg}`
          ).join('\n');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage += ': ' + error.response.data.detail;
        }
      }
      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleSavePlantilla = async () => {
    // Validaciones
    if (!plantillaFormData.nombre?.trim()) {
      alert('Por favor, introduce un nombre para la plantilla');
      return;
    }
    
    try {
      // Crear payload sin tipo_menu (ya no es necesario)
      const payload = {
        nombre: plantillaFormData.nombre,
        descripcion: plantillaFormData.descripcion || '',
        material_fijo: plantillaFormData.material_fijo || [],
        material_por_persona: plantillaFormData.material_por_persona || [],
        textiles: plantillaFormData.textiles || [],
        servicios: plantillaFormData.servicios || [],  // NUEVO: incluir servicios
        activo: plantillaFormData.activo !== false
      };
      
      if (editingPlantilla) {
        await plantillasAPI.update(editingPlantilla.id, payload);
      } else {
        await plantillasAPI.create(payload);
      }
      
      setShowPlantillaModal(false);
      fetchData();
      alert('Plantilla guardada correctamente');
    } catch (error) {
      console.error('Error saving plantilla:', error);
      let errorMessage = 'Error al guardar la plantilla';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage += ':\n' + error.response.data.detail.map(e => 
            `- ${e.loc?.join('.') || 'Campo'}: ${e.msg}`
          ).join('\n');
        } else {
          errorMessage += ': ' + error.response.data.detail;
        }
      }
      alert(errorMessage);
    }
  };

  const handleDeletePlantilla = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar esta plantilla?')) {
      try {
        await plantillasAPI.delete(id);
        fetchData();
      } catch (error) {
        console.error('Error deleting plantilla:', error);
      }
    }
  };

  const applyPlantilla = (plantillaId) => {
    // Si plantillaId está vacío, quitar la plantilla
    if (!plantillaId) {
      setFormData({
        ...formData,
        plantilla_menaje_id: null,
        material_fijo: [],
        material_por_persona: [],
        textiles: []
      });
      return;
    }
    
    const plantilla = plantillas.find(p => p.id === plantillaId);
    if (plantilla) {
      // Verificar si ya hay materiales existentes
      const tieneMaterialExistente = 
        (formData.material_fijo && formData.material_fijo.length > 0) ||
        (formData.material_por_persona && formData.material_por_persona.length > 0) ||
        (formData.textiles && formData.textiles.length > 0);
      
      if (tieneMaterialExistente) {
        // Preguntar si quiere añadir o reemplazar
        const accion = window.confirm(
          `Ya tienes material asignado a este menú.\n\n` +
          `¿Quieres AÑADIR el material de "${plantilla.nombre}" al existente?\n\n` +
          `• Pulsa ACEPTAR para AÑADIR (mantiene el material actual)\n` +
          `• Pulsa CANCELAR para REEMPLAZAR (borra el material actual)`
        );
        
        if (accion) {
          // AÑADIR: combinar materiales existentes con los de la plantilla
          // Añadir campo "bloque" con el nombre de la plantilla a cada item nuevo
          const materialFijoConBloque = (plantilla.material_fijo || []).map(item => ({
            ...item,
            bloque: plantilla.nombre
          }));
          const materialPorPersonaConBloque = (plantilla.material_por_persona || []).map(item => ({
            ...item,
            bloque: plantilla.nombre
          }));
          const textilesConBloque = (plantilla.textiles || []).map(item => ({
            ...item,
            bloque: plantilla.nombre
          }));
          
          setFormData({
            ...formData,
            plantilla_menaje_id: plantillaId,
            material_fijo: [
              ...(formData.material_fijo || []),
              ...materialFijoConBloque
            ],
            material_por_persona: [
              ...(formData.material_por_persona || []),
              ...materialPorPersonaConBloque
            ],
            textiles: [
              ...(formData.textiles || []),
              ...textilesConBloque
            ]
          });
          alert(`Material de "${plantilla.nombre}" añadido correctamente`);
        } else {
          // REEMPLAZAR: solo usar los materiales de la plantilla con bloque
          const materialFijoConBloque = (plantilla.material_fijo || []).map(item => ({
            ...item,
            bloque: plantilla.nombre
          }));
          const materialPorPersonaConBloque = (plantilla.material_por_persona || []).map(item => ({
            ...item,
            bloque: plantilla.nombre
          }));
          const textilesConBloque = (plantilla.textiles || []).map(item => ({
            ...item,
            bloque: plantilla.nombre
          }));
          
          setFormData({
            ...formData,
            plantilla_menaje_id: plantillaId,
            material_fijo: materialFijoConBloque,
            material_por_persona: materialPorPersonaConBloque,
            textiles: textilesConBloque
          });
          alert(`Plantilla "${plantilla.nombre}" aplicada (material anterior reemplazado)`);
        }
      } else {
        // No hay material existente, simplemente aplicar con bloque
        const materialFijoConBloque = (plantilla.material_fijo || []).map(item => ({
          ...item,
          bloque: plantilla.nombre
        }));
        const materialPorPersonaConBloque = (plantilla.material_por_persona || []).map(item => ({
          ...item,
          bloque: plantilla.nombre
        }));
        const textilesConBloque = (plantilla.textiles || []).map(item => ({
          ...item,
          bloque: plantilla.nombre
        }));
        
        setFormData({
          ...formData,
          plantilla_menaje_id: plantillaId,
          material_fijo: materialFijoConBloque,
          material_por_persona: materialPorPersonaConBloque,
          textiles: textilesConBloque
        });
        alert('Plantilla aplicada correctamente');
      }
    }
  };

  const addMaterialFijo = (isPlantilla = false) => {
    const setter = isPlantilla ? setPlantillaFormData : setFormData;
    const data = isPlantilla ? plantillaFormData : formData;
    setter({
      ...data,
      material_fijo: [...data.material_fijo, { nombre: '', cantidad: 1, tipo: 'mobiliario', coste_unitario: 0, bloque: 'Manual' }]
    });
  };

  const addMaterialPorPersona = (isPlantilla = false) => {
    const setter = isPlantilla ? setPlantillaFormData : setFormData;
    const data = isPlantilla ? plantillaFormData : formData;
    setter({
      ...data,
      material_por_persona: [...data.material_por_persona, { nombre: '', cantidad_unidades: 1, personas_por_unidad: 20, coste_unitario: 0, bloque: 'Manual' }]
    });
  };

  const addTextil = (isPlantilla = false) => {
    const setter = isPlantilla ? setPlantillaFormData : setFormData;
    const data = isPlantilla ? plantillaFormData : formData;
    setter({
      ...data,
      textiles: [...data.textiles, { nombre: '', cantidad: 1, coste_lavanderia: 0, bloque: 'Manual' }]
    });
  };

  // Función para crear material rápido
  const handleCreateMaterial = async () => {
    if (!newMaterial.nombre.trim()) {
      alert('Por favor ingresa un nombre para el material');
      return;
    }
    
    try {
      const response = await materialesAPI.create({
        nombre: newMaterial.nombre,
        tipo: newMaterial.tipo,
        stock: 0,
        activo: true
      });
      
      setMateriales([...materiales, response.data]);
      setShowCreateMaterialModal(false);
      setNewMaterial({ nombre: '', tipo: 'desechable' });
      alert('Material creado exitosamente');
    } catch (error) {
      console.error('Error creating material:', error);
      alert('Error al crear material');
    }
  };

  // Obtener categorías únicas de platos
  const getCategoriasPlatos = () => {
    const categorias = new Set();
    platos.forEach(plato => {
      if (plato.categoria) {
        categorias.add(plato.categoria);
      }
    });
    return Array.from(categorias).sort();
  };

  // Filtrar platos según todos los criterios
  const getPlatosFiltrados = () => {
    return platos.filter(plato => {
      // Filtro de búsqueda por nombre (sin acentos)
      if (busquedaPlato && !normalizeText(plato.nombre).includes(normalizeText(busquedaPlato))) {
        return false;
      }
      
      // Filtro por categoría (incluyendo "Servicio" para los servicios)
      if (categoriaFiltro !== 'todos') {
        // Si el filtro es "Servicio", mostrar solo los servicios
        if (categoriaFiltro === 'Servicio') {
          if (plato.tipo !== 'servicio' && plato.categoria !== 'Servicio') {
            return false;
          }
        } else {
          // Para otras categorías, comparar normalmente
          if (plato.categoria !== categoriaFiltro) {
            return false;
          }
        }
      }
      
      // Filtro por alérgenos (solo si está activo el filtro avanzado)
      if (mostrarFiltrosAvanzados && formData.alergenos_evitar && formData.alergenos_evitar.length > 0) {
        const platoAlergenos = plato.alergenos_ids || [];
        if (formData.alergenos_evitar.some(alergenoId => platoAlergenos.includes(alergenoId))) {
          return false;
        }
      }
      
      // Filtro por características alimentarias
      if (mostrarFiltrosAvanzados && formData.caracteristicas_evitar && formData.caracteristicas_evitar.length > 0) {
        const platoCaracts = plato.caracteristicas || {};
        // Las características son un diccionario {nombre: boolean}
        for (const caract of formData.caracteristicas_evitar) {
          if (caract === 'contiene_carne' && platoCaracts.contiene_carne) return false;
          if (caract === 'derivado_animal' && platoCaracts.origen_animal) return false;
          if (caract === 'contiene_cerdo' && platoCaracts.contiene_cerdo) return false;
          if (caract === 'no_apto_embarazadas' && platoCaracts.no_apto_embarazadas) return false;
          if (caract === 'sin_lactosa' && platoCaracts.sin_lactosa === false) return false;
        }
      }
      
      // Filtro por precio máximo
      if (mostrarFiltrosAvanzados && formData.precio_maximo && (plato.coste_por_porcion ?? plato.coste_total ?? 0) > formData.precio_maximo) {
        return false;
      }
      
      return true;
    });
  };

  // Calcular coste total de platos seleccionados
  const calcularCostePlatos = () => {
    let total = 0;
    formData.platos_ids.forEach(platoId => {
      const plato = platos.find(p => p.id === platoId);
      if (plato) {
        const unidades = formData.unidades_por_plato[platoId] || 1;
        // Usar coste_por_porcion en lugar de coste_total para cálculo correcto
        total += (plato.coste_por_porcion ?? plato.coste_total ?? 0) * unidades;
      }
    });
    return total;
  };

  // Calcular coste de materiales (solo consumibles, no inventariables)
  const calcularCosteMateriales = (numPersonas = 40) => {
    let costoFijo = 0;
    let costoPorPersonaTotalizado = 0;
    const desgloseFijo = [];
    const desglosePorPersona = [];
    
    // Material fijo: cantidad × precio_unitario del material
    formData.material_fijo.forEach(item => {
      const materialCatalogo = materiales.find(m => m.nombre === item.nombre);
      // Solo usar precio_unitario para consumibles, no precio_compra de inventariables
      const tipoCoste = materialCatalogo?.tipo_coste || 'consumible';
      const precioUnitario = tipoCoste === 'consumible' 
        ? (item.coste_unitario || materialCatalogo?.precio_unitario || 0)
        : 0; // Los inventariables no se suman al coste (son activos fijos)
      const cantidad = item.cantidad || 0;
      const subtotal = cantidad * precioUnitario;
      costoFijo += subtotal;
      
      if (item.nombre) {
        desgloseFijo.push({
          nombre: item.nombre,
          cantidad,
          precioUnitario,
          subtotal,
          tipoCoste
        });
      }
    });
    
    // Material por persona: calcular cuántas unidades se necesitan según el ratio
    formData.material_por_persona.forEach(item => {
      const materialCatalogo = materiales.find(m => m.nombre === item.nombre);
      const tipoCoste = materialCatalogo?.tipo_coste || 'consumible';
      const precioUnitario = tipoCoste === 'consumible'
        ? (item.coste_unitario || materialCatalogo?.precio_unitario || 0)
        : 0;
      const cantidadUnidades = item.cantidad_unidades || 1;
      const personasPorUnidad = item.personas_por_unidad || 1;
      const unidadesNecesarias = Math.ceil(numPersonas / personasPorUnidad) * cantidadUnidades;
      const subtotal = unidadesNecesarias * precioUnitario;
      costoPorPersonaTotalizado += subtotal;
      
      if (item.nombre) {
        desglosePorPersona.push({
          nombre: item.nombre,
          unidadesNecesarias,
          precioUnitario,
          subtotal,
          tipoCoste,
          ratio: `${cantidadUnidades} cada ${personasPorUnidad} pax`
        });
      }
    });
    
    const costoTextiles = formData.textiles.reduce((sum, item) => 
      sum + ((item.cantidad || 0) * (item.coste_lavanderia || 0)), 0
    );
    
    const totalMateriales = costoFijo + costoPorPersonaTotalizado + costoTextiles;
    return {
      costoFijo,
      costoPorPersona: costoPorPersonaTotalizado,
      costoTextiles,
      totalMateriales,
      costoPorPersonaUnitario: (totalMateriales / numPersonas).toFixed(2),
      desgloseFijo,
      desglosePorPersona
    };
  };

  // Calcular coste de personal
  const calcularCostePersonal = (numPersonas = 40) => {
    const numCamareros = personalPorRatio(numPersonas, formData.ratio_camareros);
    // Solo calcular extras si se supera el ratio configurado
    const numExtras = personalPorRatio(numPersonas, formData.ratio_extras, true);
    
    // Personal propio: SIEMPRE 1 persona
    // Coste = tiempo_total_evento × 0.26€/minuto
    const tiempoTotalMin = (formData.tiempo_montaje_min || 60) + 
                           (formData.duracion_evento_min || 60) + 
                           (formData.tiempo_desmontaje_min || 60);
    const costePersonalPropio = tiempoTotalMin * 0.26;
    
    // Camareros: 25€/hora
    const costeCamareros = numCamareros * (formData.horas_por_camarero || 4) * 25;
    
    // Extras: 25€/hora (solo si se supera el ratio)
    const costeExtras = numExtras * (formData.horas_por_camarero || 4) * 25;
    
    const totalPersonal = numCamareros + numExtras + 1; // +1 personal propio
    const costeTotalPersonal = costePersonalPropio + costeCamareros + costeExtras;
    
    return {
      numCamareros,
      numExtras,
      totalPersonal,
      costePersonalPropio,
      costeCamareros,
      costeExtras,
      costeTotalPersonal,
      costePorPersona: (costeTotalPersonal / numPersonas).toFixed(2)
    };
  };

  // Calcular coste de bebidas incluidas (usando Coca-Cola como referencia)
  const calcularCosteBebidas = () => {
    // Solo calcular si bebida_incluida está activada
    if (!formData.bebida_incluida) {
      return { costePorPersona: 0, costeReferencia: 0, nombreReferencia: 'N/A', bebidasPorPersona: 0 };
    }
    
    const bebidasPorPersona = formData.bebidas_por_persona || 1;
    
    // Buscar la Coca-Cola en el catálogo de platos (bebidas)
    const cocaCola = platos.find(p => 
      p.nombre && p.nombre.toLowerCase() === 'coca-cola'
    );
    
    const costeReferencia = cocaCola ? (cocaCola.coste_por_porcion ?? cocaCola.coste_total ?? 0) : 0;
    const costePorPersona = bebidasPorPersona * costeReferencia;
    
    return {
      costePorPersona,
      costeReferencia,
      nombreReferencia: cocaCola ? cocaCola.nombre : 'No encontrado',
      bebidasPorPersona
    };
  };

  // Funciones para manejar platos en el nuevo diseño
  const handleAddPlato = (platoId) => {
    if (!formData.platos_ids.includes(platoId)) {
      setFormData({
        ...formData,
        platos_ids: [...formData.platos_ids, platoId],
        unidades_por_plato: {
          ...formData.unidades_por_plato,
          [platoId]: 1
        }
      });
    }
  };

  const handleRemovePlato = (platoId) => {
    const newUnidades = { ...formData.unidades_por_plato };
    delete newUnidades[platoId];
    setFormData({
      ...formData,
      platos_ids: formData.platos_ids.filter(id => id !== platoId),
      unidades_por_plato: newUnidades
    });
  };

  const handleMovePlatoUp = (platoId) => {
    const newPlatosIds = [...formData.platos_ids];
    const currentIndex = newPlatosIds.indexOf(platoId);
    if (currentIndex <= 0) return; // Ya está al principio
    
    // Intercambiar con el elemento anterior
    [newPlatosIds[currentIndex - 1], newPlatosIds[currentIndex]] = [newPlatosIds[currentIndex], newPlatosIds[currentIndex - 1]];
    setFormData({
      ...formData,
      platos_ids: newPlatosIds
    });
  };

  const handleMovePlatoDown = (platoId) => {
    const newPlatosIds = [...formData.platos_ids];
    const currentIndex = newPlatosIds.indexOf(platoId);
    if (currentIndex === -1 || currentIndex >= newPlatosIds.length - 1) return; // Ya está al final
    
    // Intercambiar con el elemento siguiente
    [newPlatosIds[currentIndex], newPlatosIds[currentIndex + 1]] = [newPlatosIds[currentIndex + 1], newPlatosIds[currentIndex]];
    setFormData({
      ...formData,
      platos_ids: newPlatosIds
    });
  };

  const handleChangeUnidades = (platoId, unidades) => {
    setFormData({
      ...formData,
      unidades_por_plato: {
        ...formData.unidades_por_plato,
        [platoId]: parseFloat(unidades) || 1
      }
    });
  };

  const getPlatosSeleccionados = () => {
    return formData.platos_ids
      .map(platoId => platos.find(p => p.id === platoId))
      .filter(Boolean);
  };

  const getServiciosSeleccionados = () => {
    return getPlatosSeleccionados().filter(p => p.tipo === 'servicio');
  };

  const getPlatosComidaBebidaSeleccionados = () => {
    return getPlatosSeleccionados().filter(p => p.tipo !== 'servicio');
  };

  const filteredMenus = menus.filter(menu => {
    const matchesSearch = normalizeText(menu.nombre).includes(normalizeText(searchTerm));
    const matchesTipo = menu.tipo === filtroTipo;
    const matchesEstado = filtroEstado === 'todos' || 
                          (filtroEstado === 'activo' && menu.activo) || 
                          (filtroEstado === 'inactivo' && !menu.activo);
    const matchesRevisado = filtroRevisado === 'todos' || 
                            (filtroRevisado === 'revisado' && menu.revisado) || 
                            (filtroRevisado === 'sin_revisar' && !menu.revisado);
    return matchesSearch && matchesTipo && matchesEstado && matchesRevisado;
  });

  const filteredPlantillas = plantillas.filter(p =>
    normalizeText(p.nombre).includes(normalizeText(searchTerm))
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64">Cargando...</div>;
  }

  return (
    <div className="p-2 sm:p-6">
      {/* Overlay de guardado */}
      <LoadingOverlay 
        isLoading={saving} 
        message={editingMenu ? "Actualizando menú..." : "Creando menú..."} 
      />
      
      {/* Header responsive */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Menús</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Gestión de menús y plantillas de menaje</p>
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          {/* Toggle View Mode */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('menus')}
              className={`px-2 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                viewMode === 'menus'
                  ? 'bg-white text-duetto-primary shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <UtensilsCrossed className="w-4 h-4 inline sm:mr-2" />
              <span className="hidden sm:inline">Menus</span>
            </button>
            <button
              onClick={() => setViewMode('plantillas')}
              className={`px-2 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                viewMode === 'plantillas'
                  ? 'bg-white text-duetto-primary shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Layers className="w-4 h-4 inline sm:mr-2" />
              <span className="hidden sm:inline">Plantillas</span>
            </button>
            <button
              onClick={() => setViewMode('estadisticas')}
              data-testid="view-estadisticas-btn"
              className={`px-2 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                viewMode === 'estadisticas'
                  ? 'bg-white text-duetto-primary shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <DollarSign className="w-4 h-4 inline sm:mr-2" />
              <span className="hidden sm:inline">Estadisticas</span>
            </button>
          </div>

          <div className="relative group">
            <button
              onClick={() => setShowCatalogoModal(true)}
              className="flex items-center space-x-1 sm:space-x-2 bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-green-700 text-sm"
              data-testid="export-catalogo-btn"
            >
              <Download className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">Exportar Menús PDF</span>
              <span className="sm:hidden">PDF</span>
            </button>
          </div>
          <button
            onClick={() => viewMode === 'menus' ? openModal() : openPlantillaModal()}
            className="flex items-center space-x-1 sm:space-x-2 bg-duetto-primary text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-opacity-90 text-sm"
          >
            <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">{viewMode === 'menus' ? 'Nuevo Menú' : 'Nueva Plantilla'}</span>
            <span className="sm:hidden">Nuevo</span>
          </button>
        </div>
      </div>

      {/* Tabs para filtrar por tipo */}
      {viewMode !== 'estadisticas' && (<>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-1.5 sm:p-2 mb-4">
        <div className="flex space-x-1 sm:space-x-2">
          <button
            onClick={() => setFiltroTipo('menu')}
            className={`flex-1 px-2 sm:px-4 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base ${
              filtroTipo === 'menu'
                ? 'bg-duetto-primary text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <span className="hidden sm:inline">🍽️ </span>Menús ({menus.filter(m => m.tipo === 'menu').length})
          </button>
          <button
            onClick={() => setFiltroTipo('servicio')}
            className={`flex-1 px-2 sm:px-4 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base ${
              filtroTipo === 'servicio'
                ? 'bg-duetto-primary text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <span className="hidden sm:inline">⚙️ </span>Suplementos ({menus.filter(m => m.tipo === 'servicio').length})
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder={`Buscar ${viewMode === 'menus' ? 'menús' : 'plantillas'}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary text-sm sm:text-base"
          />
        </div>
        
        {/* Filtros - responsive */}
        <div className="flex flex-wrap gap-2 sm:gap-3 items-center">
          <span className="text-xs sm:text-sm font-medium text-gray-700 w-full sm:w-auto">Filtros:</span>
          
          {/* Filtro Estado */}
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="flex-1 sm:flex-none px-2 sm:px-3 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
          >
            <option value="todos">📋 Todos</option>
            <option value="activo">✅ Activos</option>
            <option value="inactivo">❌ Inactivos</option>
          </select>
          
          {/* Filtro Revisado */}
          <select
            value={filtroRevisado}
            onChange={(e) => setFiltroRevisado(e.target.value)}
            className="flex-1 sm:flex-none px-2 sm:px-3 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
          >
            <option value="todos">📝 Todos</option>
            <option value="revisado">✓ Revisados</option>
            <option value="sin_revisar">⚠ Sin revisar</option>
          </select>
          
          {/* Contador de resultados */}
          <span className="text-xs sm:text-sm text-gray-500 w-full sm:w-auto sm:ml-auto mt-1 sm:mt-0">
            {filteredMenus.length} {filteredMenus.length === 1 ? 'menú' : 'menús'}
          </span>
        </div>
      </div>
      </>)}

      {/* VISTA DE MENÚS */}
      {viewMode === 'menus' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMenus.map(menu => {
            // Obtener platos del menú usando la estructura correcta
            const platosDelMenu = (menu.platos || []).map(platoRef => {
              const plato = platos.find(p => p.id === platoRef.plato_id);
              return plato ? { ...plato, cantidad: platoRef.cantidad } : null;
            }).filter(Boolean);
            
            // Calcular coste de ingredientes en tiempo real
            const costeIngredientesReal = platosDelMenu.reduce((total, plato) => {
              const costeUnitario = plato.coste_por_porcion ?? plato.coste_total ?? 0;
              return total + (costeUnitario * (plato.cantidad || 1));
            }, 0);
            
            // Calcular coste de bebidas incluidas (usando Coca-Cola como referencia)
            // Solo si bebida_incluida está activada (no solo si bebidas_por_persona > 0)
            const bebidaIncluida = menu.bebida_incluida || false;
            const bebidasPorPersona = menu.bebidas_por_persona || 1;
            let costeBebidas = 0;
            if (bebidaIncluida) {
              const cocaCola = platos.find(p => p.nombre && p.nombre.toLowerCase() === 'coca-cola');
              const costeRefBebida = cocaCola ? (cocaCola.coste_por_porcion ?? cocaCola.coste_total ?? 0) : 0;
              costeBebidas = bebidasPorPersona * costeRefBebida;
            }
            
            // Coste total ingredientes + bebidas (solo si bebida está activada)
            const costeTotalIngredientesBebidas = costeIngredientesReal + costeBebidas;
            
            const totalAlergenos = [...new Set(platosDelMenu.flatMap(p => p.alergenos_ids || []))];

            return (
              <div key={menu.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                {/* Image placeholder */}
                <div className="bg-gradient-to-br from-duetto-primary to-duetto-secondary h-32 flex items-center justify-center">
                  <UtensilsCrossed className="w-16 h-16 text-white opacity-75" />
                </div>

                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-lg font-bold text-gray-900">{menu.nombre}</h3>
                    <div className="flex flex-col gap-2">
                      {/* Toggle Switch Activo/Inactivo */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-600">
                          {menu.activo ? 'Activo' : 'Inactivo'}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => handleToggleActivo(menu.id, e)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-duetto-primary focus:ring-offset-1 ${
                            menu.activo ? 'bg-green-500' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform ${
                              menu.activo ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                      {menu.es_alquiler && (
                        <span className="px-2 py-1 text-xs rounded-full font-semibold bg-purple-100 text-purple-700 text-center" data-testid={`menu-badge-alquiler-${menu.id}`}>
                          ALQUILER
                        </span>
                      )}
                      {/* Badge Revisado */}
                      <span className={`px-2 py-1 text-xs rounded-full font-medium ${menu.revisado ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                        {menu.revisado ? '✓ Revisado' : '⚠ Sin revisar'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center text-sm text-gray-600 mb-3">
                    <UtensilsCrossed className="w-4 h-4 mr-1" />
                    <span>{platosDelMenu.length} platos</span>
                    {menu.formato_servicio && (
                      <>
                        <span className="mx-2">•</span>
                        <span className="capitalize">{menu.formato_servicio}</span>
                      </>
                    )}
                  </div>

                  {/* Lista de platos con cantidades */}
                  {platosDelMenu.length > 0 && (
                    <div className="mb-3 border-t pt-3">
                      {(() => {
                        const platosComida = platosDelMenu.filter(p => p.tipo !== 'servicio');
                        const servicios = platosDelMenu.filter(p => p.tipo === 'servicio');
                        
                        return (
                          <>
                            {/* Platos de comida */}
                            {platosComida.length > 0 && (
                              <>
                                <p className="text-xs font-medium text-gray-500 mb-2">🍽️ Platos:</p>
                                <div className="space-y-1 max-h-24 overflow-y-auto mb-2">
                                  {platosComida.map((plato, idx) => (
                                    <div key={idx} className="flex justify-between text-xs text-gray-600">
                                      <span className="truncate flex-1">{plato.nombre}</span>
                                      <span className="ml-2 font-medium text-duetto-primary">× {plato.cantidad}</span>
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                            
                            {/* Servicios */}
                            {servicios.length > 0 && (
                              <>
                                <p className="text-xs font-medium text-gray-500 mb-2">⚙️ Servicios:</p>
                                <div className="space-y-1 max-h-20 overflow-y-auto">
                                  {servicios.map((plato, idx) => (
                                    <div key={idx} className="flex justify-between text-xs text-blue-600">
                                      <span className="truncate flex-1">{plato.nombre}</span>
                                      <span className="ml-2 font-medium">× {plato.cantidad}</span>
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}

                  {totalAlergenos.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1">Alérgenos:</p>
                      <div className="flex flex-wrap gap-1">
                        {totalAlergenos.slice(0, 6).map(alergId => {
                          const alergeno = alergenos.find(a => a.id === alergId);
                          return alergeno ? (
                            <AlergenoIcon 
                              key={alergId} 
                              nombre={alergeno.nombre} 
                              size="sm"
                            />
                          ) : null;
                        })}
                        {totalAlergenos.length > 6 && (
                          <span className="text-xs text-gray-500 self-center">+{totalAlergenos.length - 6}</span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="border-t pt-3 mt-3">
                    <div className="flex justify-between items-center text-sm mb-1">
                      <span className="text-gray-600">Precio por persona</span>
                      <span className="text-xl font-bold text-duetto-primary">€{(menu.precio_venta || menu.precio_persona || 0).toFixed(2)}</span>
                    </div>
                    {(() => {
                      // Mostrar coste de ingredientes + bebidas calculado en tiempo real
                      if (costeTotalIngredientesBebidas > 0) {
                        const precio = menu.precio_venta || menu.precio_persona || 0;
                        // Margen basado en ingredientes + bebidas
                        const margenBruto = precio > 0 ? ((precio - costeTotalIngredientesBebidas) / precio * 100) : 0;
                        
                        return (
                          <>
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-600">Coste ingredientes</span>
                              <span>€{costeIngredientesReal.toFixed(2)}</span>
                            </div>
                            {bebidaIncluida && costeBebidas > 0 && (
                              <div className="flex justify-between items-center text-sm">
                                <span className="text-gray-600">Coste bebidas ({bebidasPorPersona}×)</span>
                                <span>€{costeBebidas.toFixed(2)}</span>
                              </div>
                            )}
                            <div className="flex justify-between items-center text-sm border-t border-gray-200 pt-1 mt-1">
                              <span className="text-gray-700 font-medium">Margen bruto</span>
                              <span className={`font-semibold ${margenBruto > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {margenBruto.toFixed(1)}%
                              </span>
                            </div>
                          </>
                        );
                      }
                      return null;
                    })()}
                  </div>

                  <div className="flex space-x-2 mt-4">
                    <button
                      onClick={() => handleToggleRevisado(menu.id)}
                      className={`flex items-center justify-center px-3 py-2 rounded transition-colors ${
                        menu.revisado 
                          ? 'bg-green-50 text-green-700 hover:bg-green-100' 
                          : 'bg-orange-50 text-orange-700 hover:bg-orange-100'
                      }`}
                      title={menu.revisado ? 'Marcar como no revisado' : 'Marcar como revisado'}
                    >
                      <CheckCircle className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => openModal(menu)}
                      className="flex-1 flex items-center justify-center space-x-1 bg-gray-100 text-gray-700 px-3 py-2 rounded hover:bg-gray-200"
                    >
                      <Edit className="w-4 h-4" />
                      <span>Editar</span>
                    </button>
                    <button
                      onClick={() => handleDelete(menu.id)}
                      className="flex items-center justify-center bg-red-50 text-red-600 px-3 py-2 rounded hover:bg-red-100"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* VISTA DE PLANTILLAS */}
      {viewMode === 'plantillas' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPlantillas.map(plantilla => (
            <div key={plantilla.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
              <div className="bg-gradient-to-br from-purple-500 to-indigo-600 h-32 flex items-center justify-center">
                <Layers className="w-16 h-16 text-white opacity-75" />
              </div>

              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-bold text-gray-900">{plantilla.nombre}</h3>
                  <span className={`px-2 py-1 text-xs rounded-full ${plantilla.activo ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {plantilla.activo ? 'Activo' : 'Inactivo'}
                  </span>
                </div>

                {plantilla.descripcion && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{plantilla.descripcion}</p>
                )}

                <div className="border-t pt-3 mt-3 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Material Fijo</span>
                    <span className="font-medium">{plantilla.material_fijo?.length || 0} items</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Material/Persona</span>
                    <span className="font-medium">{plantilla.material_por_persona?.length || 0} items</span>
                  </div>
                </div>

                <div className="flex space-x-2 mt-4">
                  <button
                    onClick={() => openPlantillaModal(plantilla)}
                    className="flex-1 flex items-center justify-center space-x-1 bg-gray-100 text-gray-700 px-3 py-2 rounded hover:bg-gray-200"
                  >
                    <Edit className="w-4 h-4" />
                    <span>Editar</span>
                  </button>
                  <button
                    onClick={() => handleDeletePlantilla(plantilla.id)}
                    className="flex items-center justify-center bg-red-50 text-red-600 px-3 py-2 rounded hover:bg-red-100"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* VISTA DE ESTADÍSTICAS */}
      {viewMode === 'estadisticas' && (
        <MenuEstadisticas />
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingMenu ? (
                  <>
                    Editar Menú: <span className="text-duetto-primary">{editingMenu.nombre}</span>
                  </>
                ) : (
                  'Nuevo Menú'
                )}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Tabs */}
            <div className="border-b">
              <nav className="flex">
                <button
                  onClick={() => setActiveTab('general')}
                  className={`px-6 py-3 text-sm font-medium ${
                    activeTab === 'general'
                      ? 'border-b-2 border-duetto-primary text-duetto-primary bg-duetto-primary bg-opacity-5'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  General y Platos
                </button>
                <button
                  onClick={() => setActiveTab('material')}
                  className={`px-6 py-3 text-sm font-medium ${
                    activeTab === 'material'
                      ? 'border-b-2 border-duetto-primary text-duetto-primary bg-duetto-primary bg-opacity-5'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Material y Menaje
                </button>
                <button
                  onClick={() => setActiveTab('personal')}
                  className={`px-6 py-3 text-sm font-medium ${
                    activeTab === 'personal'
                      ? 'border-b-2 border-duetto-primary text-duetto-primary bg-duetto-primary bg-opacity-5'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Personal
                </button>
                <button
                  onClick={() => setActiveTab('tiempos')}
                  className={`px-6 py-3 text-sm font-medium ${
                    activeTab === 'tiempos'
                      ? 'border-b-2 border-duetto-primary text-duetto-primary bg-duetto-primary bg-opacity-5'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Tiempos y Logística
                </button>
                <button
                  onClick={() => setActiveTab('costes')}
                  className={`px-6 py-3 text-sm font-medium ${
                    activeTab === 'costes'
                      ? 'border-b-2 border-duetto-primary text-duetto-primary bg-duetto-primary bg-opacity-5'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Costes y Pricing
                </button>
              </nav>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
{/* Tab 1: General y Platos - REDISEÑADO */}
{activeTab === 'general' && (
  <div className="space-y-6">
    
    {/* Sección 1: Información Básica - Compacta */}
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <span>📋</span> Información Básica
      </h3>
      
      {/* Selector Menú / Servicio */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">¿Qué estás creando?</label>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setFormData({ ...formData, tipo: 'menu' })}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
              formData.tipo === 'menu'
                ? 'bg-duetto-primary text-white shadow-md'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            📋 Menú
          </button>
          <button
            type="button"
            onClick={() => setFormData({ ...formData, tipo: 'servicio' })}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
              formData.tipo === 'servicio'
                ? 'bg-duetto-primary text-white shadow-md'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            ⚙️ Suplemento
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del {formData.tipo === 'servicio' ? 'Suplemento' : 'Menú'}</label>
          <input
            type="text"
            value={formData.nombre}
            onChange={(e) => setFormData({...formData, nombre: e.target.value})}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-duetto-primary focus:border-duetto-primary"
            placeholder={formData.tipo === 'servicio' ? 'Ej: Barra Libre 3 Horas' : 'Ej: Menú Ejecutivo'}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Menú</label>
          <select
            value={formData.tipo_menu}
            onChange={(e) => setFormData({...formData, tipo_menu: e.target.value})}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-duetto-primary focus:border-duetto-primary"
          >
            <option value="">-- Seleccionar --</option>
            <option value="desayuno">Desayuno / Coffee</option>
            <option value="lunch">Lunch</option>
            <option value="coctel">Cóctel</option>
            <option value="vino_espanol">Vino Español</option>
            <option value="cena">Cena</option>
            <option value="otros">Otros</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Formato de Servicio</label>
          <select
            value={formData.formato_servicio}
            onChange={(e) => setFormData({...formData, formato_servicio: e.target.value})}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-duetto-primary focus:border-duetto-primary"
          >
            <option value="autoservicio">Autoservicio</option>
            <option value="servido">Servido con camareros</option>
          </select>
        </div>
      </div>
      
      {/* Toggle Switch para Activo/Inactivo */}
      <div className="mt-4 flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${formData.activo ? 'bg-green-500' : 'bg-gray-400'}`}></div>
          <div>
            <label className="text-sm font-medium text-gray-900">Estado del Menú</label>
            <p className="text-xs text-gray-500">
              {formData.activo 
                ? 'Activo - Visible al crear nuevos presupuestos' 
                : 'Inactivo - Solo visible en presupuestos existentes'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setFormData({...formData, activo: !formData.activo})}
          className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-duetto-primary focus:ring-offset-2 ${
            formData.activo ? 'bg-green-500' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${
              formData.activo ? 'translate-x-7' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
      
      {/* Toggle Switch para Bebida Incluida */}
      <div className="mt-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${formData.bebida_incluida ? 'bg-blue-500' : 'bg-gray-400'}`}></div>
          <div>
            <label className="text-sm font-medium text-gray-900">🥤 Bebida Incluida</label>
            <p className="text-xs text-gray-500">
              {formData.bebida_incluida 
                ? `Este menú incluye ${formData.bebidas_por_persona} bebida(s) por persona` 
                : 'Este menú no incluye bebida'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {formData.bebida_incluida && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600">Cantidad:</label>
              <input
                type="number"
                min="1"
                max="5"
                value={formData.bebidas_por_persona}
                onChange={(e) => setFormData({...formData, bebidas_por_persona: e.target.value === '' ? '' : (parseInt(e.target.value) || 1)})}
                onBlur={(e) => { if (e.target.value === '') setFormData({...formData, bebidas_por_persona: 1}); }}
                className="w-16 border border-gray-300 rounded px-2 py-1 text-sm text-center"
              />
            </div>
          )}
          <button
            type="button"
            onClick={() => setFormData({...formData, bebida_incluida: !formData.bebida_incluida})}
            className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              formData.bebida_incluida ? 'bg-blue-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${
                formData.bebida_incluida ? 'translate-x-7' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>
    </div>

    {/* Sección 2: Platos y Servicios Seleccionados - Layout Dinámico y Compacto */}
    <div className={`grid ${getServiciosSeleccionados().length > 0 ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
      
      {/* Columna: PLATOS (Comida/Bebida) - Estilo Lista Compacto */}
      <div className="bg-white border border-gray-300 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-300 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
            <span>🍽️</span> Platos
          </h3>
          <span className="text-xs font-medium text-gray-600 bg-gray-200 px-2 py-1 rounded">
            {getPlatosComidaBebidaSeleccionados().length}
          </span>
        </div>

        <div className="max-h-80 overflow-y-auto">
          {getPlatosComidaBebidaSeleccionados().length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p className="text-sm">No hay platos seleccionados</p>
              <p className="text-xs mt-1">Usa el botón de abajo para añadir</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {getPlatosComidaBebidaSeleccionados().map((plato, index) => {
                const unidades = formData.unidades_por_plato[plato.id] || 1;
                const costeTotal = (plato.coste_por_porcion ?? plato.coste_total ?? 0) * unidades;
                const platoIndex = formData.platos_ids.indexOf(plato.id);
                const isFirst = platoIndex === 0;
                const isLast = platoIndex === formData.platos_ids.length - 1;
                return (
                  <div key={plato.id} className="px-4 py-2 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between gap-3">
                      {/* Botones de Orden */}
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => handleMovePlatoUp(plato.id)}
                          disabled={isFirst}
                          className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition"
                          title="Mover arriba"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMovePlatoDown(plato.id)}
                          disabled={isLast}
                          className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition"
                          title="Mover abajo"
                        >
                          ↓
                        </button>
                      </div>
                      
                      {/* Nombre y Categoría */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{plato.nombre}</p>
                        {plato.categoria && (
                          <span className="text-xs text-gray-500">{plato.categoria}</span>
                        )}
                      </div>
                      
                      {/* Unidades */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <input
                          type="number"
                          min="0.1"
                          step="0.1"
                          value={unidades}
                          onChange={(e) => handleChangeUnidades(plato.id, e.target.value)}
                          className="w-14 border border-gray-300 rounded px-1 py-1 text-xs text-center focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <span className="text-xs text-gray-500">u/p</span>
                      </div>
                      
                      {/* Precio */}
                      <span className="text-sm font-semibold text-green-600 flex-shrink-0 w-16 text-right">
                        €{costeTotal.toFixed(2)}
                      </span>
                      
                      {/* Botón Eliminar */}
                      <button
                        type="button"
                        onClick={() => handleRemovePlato(plato.id)}
                        className="text-gray-400 hover:text-red-600 hover:bg-red-50 rounded p-1 flex-shrink-0"
                        title="Eliminar"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {getPlatosComidaBebidaSeleccionados().length > 0 && (
          <div className="bg-gray-50 px-4 py-2 border-t-2 border-gray-300 flex justify-between items-center">
            <span className="text-sm font-semibold text-gray-700">TOTAL PLATOS:</span>
            <span className="text-base font-bold text-green-600">
              €{getPlatosComidaBebidaSeleccionados().reduce((sum, plato) => {
                const unidades = formData.unidades_por_plato[plato.id] || 1;
                return sum + (plato.coste_por_porcion ?? plato.coste_total ?? 0) * unidades;
              }, 0).toFixed(2)}
            </span>
          </div>
        )}
      </div>

      {/* Columna: SERVICIOS - Solo se muestra si hay servicios */}
      {getServiciosSeleccionados().length > 0 && (
        <div className="bg-white border border-gray-300 rounded-lg overflow-hidden">
          <div className="bg-blue-50 px-4 py-2 border-b border-blue-200 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-blue-900 flex items-center gap-2">
              <span>⚙️</span> Servicios
            </h3>
            <span className="text-xs font-medium text-blue-700 bg-blue-100 px-2 py-1 rounded">
              {getServiciosSeleccionados().length}
            </span>
          </div>

          <div className="max-h-80 overflow-y-auto">
            <div className="divide-y divide-gray-200">
              {getServiciosSeleccionados().map((servicio, index) => {
                const unidades = formData.unidades_por_plato[servicio.id] || 1;
                const costeTotal = (servicio.coste_por_porcion ?? servicio.coste_total ?? 0) * unidades;
                const servicioIndex = formData.platos_ids.indexOf(servicio.id);
                const isFirst = servicioIndex === 0;
                const isLast = servicioIndex === formData.platos_ids.length - 1;
                return (
                  <div key={servicio.id} className="px-4 py-2 hover:bg-blue-50 transition-colors">
                    <div className="flex items-center justify-between gap-3">
                      {/* Botones de Orden */}
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => handleMovePlatoUp(servicio.id)}
                          disabled={isFirst}
                          className="p-1 rounded hover:bg-blue-200 disabled:opacity-30 disabled:cursor-not-allowed transition text-blue-600"
                          title="Mover arriba"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMovePlatoDown(servicio.id)}
                          disabled={isLast}
                          className="p-1 rounded hover:bg-blue-200 disabled:opacity-30 disabled:cursor-not-allowed transition text-blue-600"
                          title="Mover abajo"
                        >
                          ↓
                        </button>
                      </div>
                      
                      {/* Nombre */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{servicio.nombre}</p>
                        <span className="text-xs text-blue-600">servicio</span>
                      </div>
                      
                      {/* Unidades */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <input
                          type="number"
                          min="0.1"
                          step="0.1"
                          value={unidades}
                          onChange={(e) => handleChangeUnidades(servicio.id, e.target.value)}
                          className="w-14 border border-blue-300 rounded px-1 py-1 text-xs text-center focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <span className="text-xs text-gray-500">u/p</span>
                      </div>
                      
                      {/* Precio */}
                      <span className="text-sm font-semibold text-blue-600 flex-shrink-0 w-16 text-right">
                        €{costeTotal.toFixed(2)}
                      </span>
                      
                      {/* Botón Eliminar */}
                      <button
                        type="button"
                        onClick={() => handleRemovePlato(servicio.id)}
                        className="text-gray-400 hover:text-red-600 hover:bg-red-50 rounded p-1 flex-shrink-0"
                        title="Eliminar"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-blue-50 px-4 py-2 border-t-2 border-blue-200 flex justify-between items-center">
            <span className="text-sm font-semibold text-blue-900">TOTAL SERVICIOS:</span>
            <span className="text-base font-bold text-blue-600">
              €{getServiciosSeleccionados().reduce((sum, servicio) => {
                const unidades = formData.unidades_por_plato[servicio.id] || 1;
                return sum + (servicio.coste_por_porcion ?? servicio.coste_total ?? 0) * unidades;
              }, 0).toFixed(2)}
            </span>
          </div>
        </div>
      )}
    </div>

    {/* Sección 3: Añadir Platos/Servicios - Panel Expandible */}
    <div className="bg-white border border-gray-300 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setMostrarAnadirPlatos(!mostrarAnadirPlatos)}
        className="w-full p-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Plus className="w-5 h-5 text-blue-600" />
          <h3 className="text-sm font-semibold text-gray-800">Añadir Platos y Servicios</h3>
        </div>
        <span className={`text-gray-500 transform transition-transform ${mostrarAnadirPlatos ? 'rotate-180' : ''}`}>
          ▼
        </span>
      </button>

      {mostrarAnadirPlatos && (
        <div className="p-4 bg-gray-50 border-t border-gray-300">
          
          {/* Búsqueda y Categoría */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🔍 Buscar Plato</label>
              <input
                type="text"
                value={busquedaPlato}
                onChange={(e) => setBusquedaPlato(e.target.value)}
                placeholder="Escribe el nombre del plato..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">📂 Categoría</label>
              <select
                value={categoriaFiltro}
                onChange={(e) => setCategoriaFiltro(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="todos">Todas las categorías</option>
                {getCategoriasPlatos().sort((a, b) => a.localeCompare(b)).map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Botón Filtros Avanzados */}
          <button
            type="button"
            onClick={() => setMostrarFiltrosAvanzados(!mostrarFiltrosAvanzados)}
            className={`w-full px-4 py-2 rounded-lg text-sm font-medium transition-colors mb-4 ${
              mostrarFiltrosAvanzados
                ? 'bg-blue-600 text-white'
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {mostrarFiltrosAvanzados ? '▲ Ocultar Filtros Avanzados' : '▼ Mostrar Filtros Avanzados (Alérgenos, Precio, Restricciones)'}
          </button>

          {/* Panel de Filtros Avanzados (Colapsable) */}
          {mostrarFiltrosAvanzados && (
            <div className="mb-4 p-4 bg-white border border-gray-300 rounded-lg space-y-4">
              
              {/* Alérgenos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Excluir Alérgenos</label>
                <div className="grid grid-cols-7 gap-2">
                  {alergenos.map(alergeno => (
                    <button
                      key={alergeno.id}
                      type="button"
                      onClick={() => {
                        const evitar = formData.alergenos_evitar || [];
                        if (evitar.includes(alergeno.id)) {
                          setFormData({
                            ...formData,
                            alergenos_evitar: evitar.filter(id => id !== alergeno.id)
                          });
                        } else {
                          setFormData({
                            ...formData,
                            alergenos_evitar: [...evitar, alergeno.id]
                          });
                        }
                      }}
                      className={`p-2 rounded border transition-all flex items-center justify-center ${
                        formData.alergenos_evitar?.includes(alergeno.id)
                          ? 'bg-red-50 border-red-400 ring-1 ring-red-200'
                          : 'bg-gray-50 border-gray-300 hover:border-gray-400'
                      }`}
                      title={alergeno.nombre}
                    >
                      <AlergenoIcon nombre={alergeno.nombre} size="md" />
                    </button>
                  ))}
                </div>
              </div>

              {/* Restricciones Alimentarias */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Excluir Características</label>
                <div className="grid grid-cols-3 gap-2">
                  {['contiene_carne', 'derivado_animal', 'contiene_cerdo', 'no_apto_embarazadas', 'sin_lactosa'].map(caract => (
                    <button
                      key={caract}
                      type="button"
                      onClick={() => {
                        const evitar = formData.caracteristicas_evitar || [];
                        if (evitar.includes(caract)) {
                          setFormData({
                            ...formData,
                            caracteristicas_evitar: evitar.filter(c => c !== caract)
                          });
                        } else {
                          setFormData({
                            ...formData,
                            caracteristicas_evitar: [...evitar, caract]
                          });
                        }
                      }}
                      className={`px-3 py-2 rounded border text-xs font-medium transition-all ${
                        formData.caracteristicas_evitar?.includes(caract)
                          ? 'bg-red-50 border-red-400 text-red-700'
                          : 'bg-gray-50 border-gray-300 text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      {caract === 'contiene_carne' && 'Contiene Carne'}
                      {caract === 'derivado_animal' && 'Derivado Animal'}
                      {caract === 'contiene_cerdo' && 'Contiene Cerdo'}
                      {caract === 'no_apto_embarazadas' && 'No Apto Embarazadas'}
                      {caract === 'sin_lactosa' && 'Sin Lactosa'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Precio Máximo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Precio Máximo por Plato</label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={formData.precio_maximo || ''}
                  onChange={(e) => setFormData({ ...formData, precio_maximo: e.target.value ? parseFloat(e.target.value) : null })}
                  placeholder="Ej: 15.00"
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          )}

          {/* Grid de Platos Disponibles */}
          <div className="border border-gray-200 rounded-lg p-3 max-h-96 overflow-y-auto bg-gray-50">
            <div className="grid grid-cols-1 gap-2">
              {getPlatosFiltrados().map(plato => {
                const isSelected = formData.platos_ids.includes(plato.id);
                const platoAlergenos = plato.alergenos_ids || [];
                
                return (
                  <div
                    key={plato.id}
                    className={`bg-white rounded-lg p-3 border-2 transition-all ${
                      isSelected
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-green-300'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-semibold text-gray-900">{plato.nombre}</span>
                          {plato.categoria && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                              {plato.categoria}
                            </span>
                          )}
                          {plato.tipo === 'servicio' && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                              servicio
                            </span>
                          )}
                          <span className="text-sm font-bold text-green-700">
                            €{(plato.coste_por_porcion ?? plato.coste_total ?? 0).toFixed(2)}
                          </span>
                        </div>
                        
                        {platoAlergenos.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {platoAlergenos.map(alergenoId => {
                              const alergeno = alergenos.find(a => a.id === alergenoId);
                              return alergeno ? (
                                <AlergenoIcon 
                                  key={alergenoId} 
                                  nombre={alergeno.nombre} 
                                  size="sm"
                                />
                              ) : null;
                            })}
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => isSelected ? handleRemovePlato(plato.id) : handleAddPlato(plato.id)}
                        className={`px-3 py-1.5 rounded text-xs font-medium transition-colors flex-shrink-0 ${
                          isSelected
                            ? 'bg-red-100 text-red-700 border border-red-300 hover:bg-red-200'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {isSelected ? 'Quitar' : 'Añadir'}
                      </button>
                    </div>
                  </div>
                );
              })}
              
              {getPlatosFiltrados().length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>No se encontraron platos con los filtros aplicados</p>
                </div>
              )}
            </div>
          </div>

          <div className="mt-3 text-sm text-gray-600 text-center">
            Mostrando {getPlatosFiltrados().length} de {platos.length} platos disponibles
          </div>
        </div>
      )}
    </div>

  </div>
)}
              {/* Tab 2: Material y Menaje */}
              {activeTab === 'material' && (
                <div className="space-y-6">
                  {/* Selector de Plantilla */}
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-purple-900 mb-3 flex items-center">
                      <Layers className="w-5 h-5 mr-2" />
                      Aplicar Plantilla de Menaje
                    </h3>
                    <div className="grid grid-cols-12 gap-3">
                      <div className="col-span-10">
                        <select
                          value={formData.plantilla_menaje_id || ''}
                          onChange={(e) => applyPlantilla(e.target.value)}
                          className="w-full border rounded-lg px-3 py-2"
                        >
                          <option value="">-- Sin plantilla --</option>
                          {plantillas
                            .filter(p => p.activo)
                            .sort((a, b) => a.nombre.localeCompare(b.nombre))
                            .map(p => (
                              <option key={p.id} value={p.id}>
                                {p.nombre}
                              </option>
                            ))
                          }
                        </select>
                      </div>
                      <div className="col-span-2 flex gap-1">
                        {formData.plantilla_menaje_id && (
                          <button
                            type="button"
                            onClick={async () => {
                              // Recargar plantillas desde el servidor
                              try {
                                const res = await plantillasAPI.getAll();
                                const plantillasData = Array.isArray(res.data) ? res.data : [];
                                setPlantillas(plantillasData);
                                // Re-aplicar la plantilla actual con datos frescos
                                const plantillaActual = plantillasData.find(p => p.id === formData.plantilla_menaje_id);
                                if (plantillaActual) {
                                  setFormData({
                                    ...formData,
                                    material_fijo: plantillaActual.material_fijo || [],
                                    material_por_persona: plantillaActual.material_por_persona || [],
                                    textiles: plantillaActual.textiles || []
                                  });
                                  alert('Plantilla actualizada correctamente');
                                }
                              } catch (error) {
                                console.error('Error recargando plantilla:', error);
                                alert('Error al recargar la plantilla');
                              }
                            }}
                            className="flex-1 h-full bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center justify-center"
                            title="Recargar plantilla con datos actualizados"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setViewMode('plantillas')}
                          className="flex-1 h-full bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center"
                          title="Ver plantillas"
                        >
                          <Layers className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-purple-700 mt-2">
                      💡 Selecciona una plantilla para cargar automáticamente material, textiles y configuración estándar
                    </p>
                  </div>

                  {/* Material Fijo - Agrupado por Bloques */}
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-lg font-semibold text-gray-900">Material Fijo (por evento)</h3>
                      <button
                        type="button"
                        onClick={() => addMaterialFijo(false)}
                        className="text-sm bg-duetto-primary text-white px-3 py-1 rounded hover:bg-opacity-90"
                      >
                        + Añadir Manual
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">Material que se necesita independientemente del número de personas</p>
                    
                    {/* Mostrar agrupado por bloques */}
                    {agruparMaterialPorBloques(formData.material_fijo).map((grupo, grupoIdx) => (
                      <div key={grupoIdx} className="mb-4">
                        <div className={`px-3 py-1.5 rounded-t-lg font-medium text-sm ${
                          grupo.bloque === 'Manual' 
                            ? 'bg-gray-200 text-gray-700' 
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {grupo.bloque === 'Manual' ? '📝 Añadido Manualmente' : `📦 ${grupo.bloque}`}
                        </div>
                        <div className="border border-t-0 rounded-b-lg p-2 space-y-2">
                          {grupo.items.map((item) => (
                            <div key={item.originalIndex} className="flex gap-2 items-center bg-gray-50 p-2 rounded">
                              <select
                                value={item.nombre}
                                onChange={(e) => {
                                  if (e.target.value === '__CREATE_NEW__') {
                                    setShowCreateMaterialModal(true);
                                  } else {
                                    const updated = [...formData.material_fijo];
                                    updated[item.originalIndex].nombre = e.target.value;
                                    setFormData({...formData, material_fijo: updated});
                                  }
                                }}
                                className="flex-1 border rounded px-2 py-1 text-sm focus:ring-2 focus:ring-duetto-primary"
                              >
                                <option value="">-- Seleccionar material --</option>
                                {[...new Map(materiales.map(mat => [mat.nombre, mat])).values()].sort((a, b) => a.nombre.localeCompare(b.nombre)).map(mat => (
                                  <option key={mat.id} value={mat.nombre}>
                                    {mat.nombre}
                                  </option>
                                ))}
                                <option value="__CREATE_NEW__" className="font-bold text-green-600">
                                  ➕ Crear nuevo material
                                </option>
                              </select>
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  min="1"
                                  value={item.cantidad || ''}
                                  onChange={(e) => {
                                    const updated = [...formData.material_fijo];
                                    updated[item.originalIndex].cantidad = e.target.value === '' ? '' : (parseInt(e.target.value) || 1);
                                    setFormData({...formData, material_fijo: updated});
                                  }}
                                  onBlur={(e) => {
                                    if (e.target.value === '') {
                                      const updated = [...formData.material_fijo];
                                      updated[item.originalIndex].cantidad = 1;
                                      setFormData({...formData, material_fijo: updated});
                                    }
                                  }}
                                  className="w-16 border rounded px-2 py-1 text-sm text-center focus:ring-2 focus:ring-duetto-primary"
                                />
                                <span className="text-xs text-gray-500 whitespace-nowrap">uds/evento</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const updated = formData.material_fijo.filter((_, i) => i !== item.originalIndex);
                                  setFormData({...formData, material_fijo: updated});
                                }}
                                className="text-red-600 hover:text-red-800 flex items-center justify-center"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    
                    {(!formData.material_fijo || formData.material_fijo.length === 0) && (
                      <p className="text-sm text-gray-400 italic">No hay material fijo añadido</p>
                    )}
                  </div>

                  {/* Material por Persona - Agrupado por Bloques */}
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-lg font-semibold text-gray-900">Material por Nº de Personas</h3>
                      <button
                        type="button"
                        onClick={() => addMaterialPorPersona(false)}
                        className="text-sm bg-duetto-primary text-white px-3 py-1 rounded hover:bg-opacity-90"
                      >
                        + Añadir Manual
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">Ej: 1 bandeja cada 20 personas</p>
                    
                    {/* Mostrar agrupado por bloques */}
                    {agruparMaterialPorBloques(formData.material_por_persona).map((grupo, grupoIdx) => (
                      <div key={grupoIdx} className="mb-4">
                        <div className={`px-3 py-1.5 rounded-t-lg font-medium text-sm ${
                          grupo.bloque === 'Manual' 
                            ? 'bg-gray-200 text-gray-700' 
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {grupo.bloque === 'Manual' ? '📝 Añadido Manualmente' : `📦 ${grupo.bloque}`}
                        </div>
                        <div className="border border-t-0 rounded-b-lg p-2 space-y-2">
                          {grupo.items.map((item) => (
                            <div key={item.originalIndex} className="flex gap-2 items-center bg-gray-50 p-2 rounded">
                              <select
                                value={item.nombre}
                                onChange={(e) => {
                                  if (e.target.value === '__CREATE_NEW__') {
                                    setShowCreateMaterialModal(true);
                                  } else {
                                    const updated = [...formData.material_por_persona];
                                    updated[item.originalIndex].nombre = e.target.value;
                                    setFormData({...formData, material_por_persona: updated});
                                  }
                                }}
                                className="flex-1 border rounded px-2 py-1 text-sm focus:ring-2 focus:ring-duetto-primary"
                              >
                                <option value="">-- Seleccionar material --</option>
                                {[...new Map(materiales.map(mat => [mat.nombre, mat])).values()].sort((a, b) => a.nombre.localeCompare(b.nombre)).map(mat => (
                                  <option key={mat.id} value={mat.nombre}>
                                    {mat.nombre}
                                  </option>
                                ))}
                                <option value="__CREATE_NEW__" className="font-bold text-green-600">
                                  ➕ Crear nuevo material
                                </option>
                              </select>
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  min="1"
                                  value={item.cantidad || ''}
                                  onChange={(e) => {
                                    const updated = [...formData.material_por_persona];
                                    updated[item.originalIndex].cantidad = e.target.value === '' ? '' : (parseInt(e.target.value) || 1);
                                    setFormData({...formData, material_por_persona: updated});
                                  }}
                                  onBlur={(e) => {
                                    if (e.target.value === '') {
                                      const updated = [...formData.material_por_persona];
                                      updated[item.originalIndex].cantidad = 1;
                                      setFormData({...formData, material_por_persona: updated});
                                    }
                                  }}
                                  className="w-12 border rounded px-2 py-1 text-sm text-center"
                                />
                                <span className="text-xs text-gray-500">cada</span>
                                <input
                                  type="number"
                                  min="1"
                                  value={item.por_personas || ''}
                                  onChange={(e) => {
                                    const updated = [...formData.material_por_persona];
                                    updated[item.originalIndex].por_personas = e.target.value === '' ? '' : (parseInt(e.target.value) || 20);
                                    setFormData({...formData, material_por_persona: updated});
                                  }}
                                  onBlur={(e) => {
                                    if (e.target.value === '') {
                                      const updated = [...formData.material_por_persona];
                                      updated[item.originalIndex].por_personas = 20;
                                      setFormData({...formData, material_por_persona: updated});
                                    }
                                  }}
                                  className="w-12 border rounded px-2 py-1 text-sm text-center"
                                />
                                <span className="text-xs text-gray-500">pax</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const updated = formData.material_por_persona.filter((_, i) => i !== item.originalIndex);
                                  setFormData({...formData, material_por_persona: updated});
                                }}
                                className="text-red-600 hover:text-red-800 flex items-center justify-center"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    
                    {(!formData.material_por_persona || formData.material_por_persona.length === 0) && (
                      <p className="text-sm text-gray-400 italic">No hay material por persona añadido</p>
                    )}
                  </div>
                </div>
              )}

              {/* Tab 3: Personal */}
              {activeTab === 'personal' && (
                <div className="space-y-6">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-indigo-900 mb-2">👥 Configuración de Personal</h3>
                    <p className="text-sm text-indigo-700">
                      Define los ratios de personal necesario. Los cálculos se realizarán automáticamente al confirmar el presupuesto.
                    </p>
                  </div>

                  {/* Ratios de Camareros */}
                  <div className="bg-white border border-gray-300 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <Users className="w-5 h-5 mr-2 text-indigo-600" />
                      Camareros de Servicio
                    </h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Ratio Camareros / Personas
                        </label>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">1 camarero cada</span>
                          <input
                            type="number"
                            min="0"
                            max="200"
                            value={formData.ratio_camareros}
                            onChange={(e) => setFormData({...formData, ratio_camareros: parseInt(e.target.value) || 0})}
                            className="w-20 border rounded-lg px-3 py-2 text-center focus:ring-2 focus:ring-indigo-500"
                          />
                          <span className="text-sm text-gray-600">personas</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          💡 Recomendado: Servido (15), Buffet (25-30), Cóctel (20). Pon 0 si no requiere camareros
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Ratio Extras de Apoyo
                        </label>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">1 extra cada</span>
                          <input
                            type="number"
                            min="0"
                            max="500"
                            value={formData.ratio_extras}
                            onChange={(e) => setFormData({...formData, ratio_extras: parseInt(e.target.value) || 0})}
                            className="w-20 border rounded-lg px-3 py-2 text-center focus:ring-2 focus:ring-indigo-500"
                          />
                          <span className="text-sm text-gray-600">personas</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          💡 Personal extra para cocina, montaje, o apoyo general. Pon 0 si no requiere extras
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Horas Estimadas */}
                  <div className="bg-white border border-gray-300 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <Clock className="w-5 h-5 mr-2 text-green-600" />
                      Horas de Trabajo
                    </h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Horas Estimadas por Camarero
                      </label>
                      <input
                        type="number"
                        min="1"
                        step="0.5"
                        value={formData.horas_por_camarero}
                        onChange={(e) => setFormData({...formData, horas_por_camarero: parseFloat(e.target.value) || 4})}
                        className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Incluye montaje, servicio y recogida</p>
                    </div>
                  </div>

                  {/* Ejemplo de Cálculo */}
                  <div className="bg-blue-50 border border-blue-300 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-3">📊 Ejemplo de Cálculo (40 personas)</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Camareros necesarios:</span>
                        <span className="font-semibold text-blue-900">
                          {personalPorRatio(40, formData.ratio_camareros)} camareros
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Extras de apoyo:</span>
                        <span className="font-semibold text-blue-900">
                          {personalPorRatio(40, formData.ratio_extras, true)} personas
                        </span>
                      </div>
                      <div className="border-t border-blue-300 pt-2 mt-2 flex justify-between">
                        <span className="font-bold text-blue-900">TOTAL PERSONAL:</span>
                        <span className="text-xl font-bold text-blue-900">
                          {personalPorRatio(40, formData.ratio_camareros) + personalPorRatio(40, formData.ratio_extras, true)} personas
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-blue-700 mt-3">
                      ℹ️ Los costes de personal se calcularán en el presupuesto según tarifas configuradas
                    </p>
                  </div>
                </div>
              )}

              {/* Tab 4: Tiempos y Logística */}
              {activeTab === 'tiempos' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tiempo Montaje (min)</label>
                      <input
                        type="number"
                        value={formData.tiempo_montaje_min}
                        onChange={(e) => setFormData({...formData, tiempo_montaje_min: parseInt(e.target.value) || 60})}
                        className="w-full border rounded-lg px-3 py-2"
                      />
                      <p className="text-xs text-gray-500 mt-1">Desayuno: 60min, Lunch: 120min, Cóctel: 180min</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Duración Evento (min)</label>
                      <input
                        type="number"
                        value={formData.duracion_evento_min}
                        onChange={(e) => setFormData({...formData, duracion_evento_min: parseInt(e.target.value) || 60})}
                        className="w-full border rounded-lg px-3 py-2"
                      />
                      <p className="text-xs text-gray-500 mt-1">Desayuno: 60min, Cóctel grande: 120min</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tiempo Desmontaje (min)</label>
                      <input
                        type="number"
                        value={formData.tiempo_desmontaje_min}
                        onChange={(e) => setFormData({...formData, tiempo_desmontaje_min: parseInt(e.target.value) || 60})}
                        className="w-full border rounded-lg px-3 py-2"
                      />
                    </div>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-900 mb-2">Tiempo Total</h4>
                    <p className="text-2xl font-bold text-green-900">
                      {formData.tiempo_montaje_min + formData.duracion_evento_min + formData.tiempo_desmontaje_min} minutos
                    </p>
                    <p className="text-sm text-green-700">
                      ({((formData.tiempo_montaje_min + formData.duracion_evento_min + formData.tiempo_desmontaje_min) / 60).toFixed(1)} horas)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Instrucciones de Montaje</label>
                    <textarea
                      value={formData.instrucciones_montaje}
                      onChange={(e) => setFormData({...formData, instrucciones_montaje: e.target.value})}
                      rows="3"
                      className="w-full border rounded-lg px-3 py-2 resize-none"
                      placeholder="Instrucciones específicas para el montaje..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Requisitos del Lugar</label>
                    <textarea
                      value={formData.requisitos_lugar}
                      onChange={(e) => setFormData({...formData, requisitos_lugar: e.target.value})}
                      rows="3"
                      className="w-full border rounded-lg px-3 py-2 resize-none"
                      placeholder="Acceso, electricidad, agua, etc..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Requisitos del formato (aviso en el presupuesto) <span className="text-xs font-normal text-amber-700">(en pruebas)</span></label>
                    <textarea
                      value={formData.requisitos_montaje || ''}
                      onChange={(e) => setFormData({...formData, requisitos_montaje: e.target.value})}
                      rows="2"
                      className="w-full border rounded-lg px-3 py-2 resize-none"
                      placeholder="Ej.: necesita horno y una sala aparte"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Si lo rellenas, al añadir este menú a un presupuesto se muestra como aviso junto al menú.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hora en el presupuesto</label>
                    <select
                      value={formData.requiere_horario === true ? 'si' : (formData.requiere_horario === false ? 'no' : 'auto')}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFormData({ ...formData, requiere_horario: v === 'auto' ? null : v === 'si' });
                      }}
                      className="border rounded-lg px-3 py-2 text-sm"
                      data-testid="menu-requiere-horario"
                    >
                      <option value="auto">Automático</option>
                      <option value="si">Servicio con montaje: lleva hora</option>
                      <option value="no">Complemento: acompaña a otro servicio</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Automático: los formatos (coffee, lunch, cóctel...) llevan hora; bebidas, suplementos, fruta o camareros por horas acompañan a un servicio.
                    </p>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        checked={!!formData.es_alquiler}
                        onChange={(e) => setFormData({ ...formData, es_alquiler: e.target.checked })}
                        className="h-4 w-4 accent-[#1A9169]"
                        data-testid="menu-es-alquiler"
                      />
                      Es alquiler
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Lo que lleva este menú lo trae el proveedor al evento: no se carga en la nave y se comprueba en la hoja de material.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notas Especiales</label>
                    <textarea
                      value={formData.notas_especiales}
                      onChange={(e) => setFormData({...formData, notas_especiales: e.target.value})}
                      rows="3"
                      className="w-full border rounded-lg px-3 py-2 resize-none"
                      placeholder="Consideraciones adicionales..."
                    />
                  </div>
                </div>
              )}

              {/* Tab 5: Costes y Pricing */}
              {activeTab === 'costes' && (
                <div className="space-y-6">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-emerald-900 mb-2">💰 Precios y Márgenes</h3>
                    <p className="text-sm text-emerald-700">
                      Define los márgenes comerciales y precio de venta. El desglose de costes se muestra para un evento ejemplo de 40 personas.
                    </p>
                  </div>

                  {/* Configuración de Precios */}
                  <div className="bg-white border border-gray-300 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-4">⚙️ Configuración</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">IVA (%)</label>
                      <select
                        value={formData.iva}
                        onChange={(e) => setFormData({...formData, iva: parseInt(e.target.value)})}
                        className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="10">10% - Productos alimenticios</option>
                        <option value="21">21% - Servicios y personal</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Selecciona el IVA aplicable</p>
                    </div>
                  </div>

                  {/* Desglose de Costes (Ejemplo 40 personas) */}
                  <div className="bg-blue-50 border border-blue-300 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-4">📊 Desglose de Costes (Ejemplo: 40 personas)</h4>
                    <div className="space-y-3">
                      {/* Coste Platos */}
                      <div className="bg-white rounded-lg p-3">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium text-gray-700">🍽️ Coste de Platos</span>
                          <span className="text-lg font-bold text-blue-900">
                            €{calcularCostePlatos().toFixed(2)}/pers
                          </span>
                        </div>
                        <div className="text-xs text-gray-600">
                          Total 40 personas: €{(calcularCostePlatos() * 40).toFixed(2)}
                        </div>
                      </div>

                      {/* Coste Materiales */}
                      {(() => {
                        const materialesCostes = calcularCosteMateriales(40);
                        return (
                          <div className="bg-white rounded-lg p-3">
                            <div className="flex justify-between items-center mb-2">
                              <span className="font-medium text-gray-700">📦 Coste de Material (Consumibles)</span>
                              <span className="text-lg font-bold text-blue-900">
                                €{materialesCostes.costoPorPersonaUnitario}/pers
                              </span>
                            </div>
                            <div className="text-xs text-gray-600 space-y-1">
                              {/* Desglose Material Fijo */}
                              {materialesCostes.desgloseFijo.length > 0 && (
                                <div className="mb-2">
                                  <div className="font-semibold text-gray-700 mb-1">Material fijo:</div>
                                  {materialesCostes.desgloseFijo.map((item, idx) => (
                                    <div key={idx} className="flex justify-between pl-2 text-gray-500">
                                      <span>{item.cantidad}x {item.nombre} {item.tipoCoste === 'inventariable' && '(inv.)'}</span>
                                      <span>{item.subtotal > 0 ? `€${item.subtotal.toFixed(2)}` : '-'}</span>
                                    </div>
                                  ))}
                                  <div className="flex justify-between font-medium border-t border-gray-200 mt-1 pt-1">
                                    <span>Subtotal fijo:</span>
                                    <span>€{materialesCostes.costoFijo.toFixed(2)}</span>
                                  </div>
                                </div>
                              )}
                              
                              {/* Desglose Material por Persona */}
                              {materialesCostes.desglosePorPersona.length > 0 && (
                                <div className="mb-2">
                                  <div className="font-semibold text-gray-700 mb-1">Material por persona (40 pax):</div>
                                  {materialesCostes.desglosePorPersona.map((item, idx) => (
                                    <div key={idx} className="flex justify-between pl-2 text-gray-500">
                                      <span>{item.unidadesNecesarias}x {item.nombre} {item.tipoCoste === 'inventariable' && '(inv.)'}</span>
                                      <span>{item.subtotal > 0 ? `€${item.subtotal.toFixed(2)}` : '-'}</span>
                                    </div>
                                  ))}
                                  <div className="flex justify-between font-medium border-t border-gray-200 mt-1 pt-1">
                                    <span>Subtotal por persona:</span>
                                    <span>€{materialesCostes.costoPorPersona.toFixed(2)}</span>
                                  </div>
                                </div>
                              )}
                              
                              {/* Textiles */}
                              {formData.textiles?.length > 0 && (
                                <div className="flex justify-between">
                                  <span>Textiles ({formData.textiles.length} items):</span>
                                  <span>€{materialesCostes.costoTextiles.toFixed(2)}</span>
                                </div>
                              )}
                              
                              <div className="border-t border-gray-300 pt-1 mt-1 flex justify-between font-semibold text-gray-800">
                                <span>Total para 40 pax:</span>
                                <span>€{materialesCostes.totalMateriales.toFixed(2)}</span>
                              </div>
                              
                              <p className="text-[10px] text-gray-400 mt-1 italic">
                                * Solo se cuentan consumibles. Los inventariables (mesas, bandejas, etc.) no suman al coste.
                              </p>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Coste Personal */}
                      {(() => {
                        const personalInfo = calcularCostePersonal(40);
                        return (
                          <div className="bg-white rounded-lg p-3">
                            <div className="flex justify-between items-center mb-2">
                              <span className="font-medium text-gray-700">👥 Coste de Personal</span>
                              <span className="text-lg font-bold text-blue-900">
                                €{personalInfo.costePorPersona}/pers
                              </span>
                            </div>
                            <div className="text-xs text-gray-600 space-y-1">
                              <div className="flex justify-between">
                                <span>Personal propio (1 persona):</span>
                                <span>€{personalInfo.costePersonalPropio.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Camareros ({personalInfo.numCamareros} × {formData.horas_por_camarero}h × €25):</span>
                                <span>€{personalInfo.costeCamareros.toFixed(2)}</span>
                              </div>
                              {personalInfo.numExtras > 0 && (
                                <div className="flex justify-between">
                                  <span>Extras ({personalInfo.numExtras} × {formData.horas_por_camarero}h × €25):</span>
                                  <span>€{personalInfo.costeExtras.toFixed(2)}</span>
                                </div>
                              )}
                              <div className="border-t border-gray-300 pt-1 mt-1 flex justify-between font-semibold">
                                <span>Total personal:</span>
                                <span>€{personalInfo.costeTotalPersonal.toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Coste de Bebidas Incluidas */}
                      {formData.bebida_incluida && (() => {
                        const bebidasInfo = calcularCosteBebidas();
                        return (
                          <div className="bg-white rounded-lg p-3">
                            <div className="flex justify-between items-center mb-2">
                              <span className="font-medium text-gray-700">🍺 Coste de Bebidas Incluidas</span>
                              <span className="text-lg font-bold text-purple-900">
                                €{bebidasInfo.costePorPersona.toFixed(2)}/pers
                              </span>
                            </div>
                            <div className="text-xs text-gray-600 space-y-1">
                              <div className="flex justify-between">
                                <span>Bebidas por persona:</span>
                                <span>{bebidasInfo.bebidasPorPersona}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Coste referencia ({bebidasInfo.nombreReferencia}):</span>
                                <span>€{bebidasInfo.costeReferencia.toFixed(2)}</span>
                              </div>
                              <div className="border-t border-gray-300 pt-1 mt-1 text-purple-700 italic">
                                * Coste estimado usando Coca-Cola como referencia
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Coste Total Estimado */}
                      <div className="bg-emerald-100 border-2 border-emerald-400 rounded-lg p-4 mt-4">
                        <div className="flex justify-between items-center mb-3">
                          <span className="font-bold text-gray-900">💵 COSTE TOTAL ESTIMADO/PERSONA:</span>
                          <span className="text-2xl font-bold text-emerald-900">
                            €{(() => {
                              const costePlatos = calcularCostePlatos();
                              const materialesCostes = calcularCosteMateriales(40);
                              const costoMaterialPP = parseFloat(materialesCostes.costoPorPersonaUnitario);
                              const personalInfo = calcularCostePersonal(40);
                              const costoPersonalPP = parseFloat(personalInfo.costePorPersona);
                              const bebidasInfo = calcularCosteBebidas();
                              const costoBebidas = bebidasInfo.costePorPersona || 0;
                              return (costePlatos + costoMaterialPP + costoPersonalPP + costoBebidas).toFixed(2);
                            })()}
                          </span>
                        </div>
                        <div className="text-xs text-gray-700">
                          (Platos + Material/Menaje + Personal + Bebidas)  
                          <span className="block mt-1 text-amber-700">⚠️ Transporte se calculará en presupuesto con Google Maps API</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Precio de Venta */}
                  <div className="bg-white border border-gray-300 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-4">🏷️ Precio de Venta</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Precio de Venta (€/persona) *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.precio_venta}
                          onChange={(e) => setFormData({...formData, precio_venta: parseFloat(e.target.value) || 0})}
                          className="w-full border-2 rounded-lg px-4 py-3 text-2xl font-bold focus:ring-2 focus:ring-emerald-500"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          🧮 Calcular por margen
                        </label>
                        <select
                          onChange={(e) => {
                            const margenDeseado = parseInt(e.target.value);
                            if (margenDeseado > 0) {
                              // Calcular coste total
                              const costePlatos = calcularCostePlatos();
                              const materialesCostes = calcularCosteMateriales(40);
                              const costoMaterialPP = parseFloat(materialesCostes.costoPorPersonaUnitario);
                              const personalInfo = calcularCostePersonal(40);
                              const costoPersonalPP = parseFloat(personalInfo.costePorPersona);
                              const bebidasInfo = calcularCosteBebidas();
                              const costoBebidas = bebidasInfo.costePorPersona || 0;
                              const costeTotal = costePlatos + costoMaterialPP + costoPersonalPP + costoBebidas;
                              
                              // Calcular precio para ese margen: Precio = Coste / (1 - Margen/100)
                              const precioCalculado = costeTotal / (1 - margenDeseado / 100);
                              
                              // Redondear a número "bonito" (entero, .50 o .99)
                              let precioRedondeado;
                              const decimal = precioCalculado % 1;
                              if (decimal < 0.25) {
                                precioRedondeado = Math.floor(precioCalculado);
                              } else if (decimal < 0.75) {
                                precioRedondeado = Math.floor(precioCalculado) + 0.5;
                              } else {
                                precioRedondeado = Math.floor(precioCalculado) + 0.99;
                              }
                              
                              // Aplicar al campo de precio
                              setFormData({...formData, precio_venta: precioRedondeado});
                            }
                            // Reset select después de aplicar
                            e.target.value = "";
                          }}
                          className="w-full border-2 rounded-lg px-4 py-3 text-lg focus:ring-2 focus:ring-amber-500 bg-amber-50 cursor-pointer"
                          defaultValue=""
                        >
                          <option value="" disabled>Selecciona...</option>
                          <option value="20">20%</option>
                          <option value="25">25%</option>
                          <option value="30">30%</option>
                          <option value="35">35%</option>
                          <option value="40">40%</option>
                          <option value="45">45%</option>
                          <option value="50">50%</option>
                        </select>
                        <p className="text-xs text-amber-700 mt-1">⚡ Aplica precio automático</p>
                      </div>
                    </div>
                    
                    {formData.precio_venta > 0 && (
                      <div className="mt-4 bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Precio sin IVA</span>
                          <span className="font-semibold">€{formData.precio_venta.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">IVA ({formData.iva}%)</span>
                          <span>€{(formData.precio_venta * (formData.iva / 100)).toFixed(2)}</span>
                        </div>
                        <div className="border-t-2 border-gray-300 pt-2 flex justify-between">
                          <span className="font-bold text-gray-900">Precio Final con IVA</span>
                          <span className="text-2xl font-bold text-emerald-600">
                            €{(formData.precio_venta * (1 + formData.iva / 100)).toFixed(2)}
                          </span>
                        </div>
                        <div className="border-t border-gray-200 pt-2 flex justify-between text-xs">
                          <span className="text-gray-500">Para 40 personas</span>
                          <span className="font-bold text-gray-700">
                            €{(formData.precio_venta * (1 + formData.iva / 100) * 40).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* Resumen de Beneficio y Margen */}
                    {formData.precio_venta > 0 && (() => {
                      const costePlatos = calcularCostePlatos() || 0;
                      const materialesInfo = calcularCosteMateriales();
                      const costeMateriales = parseFloat(materialesInfo.costoPorPersonaUnitario) || 0;
                      const personalInfo = calcularCostePersonal();
                      const costePersonal = parseFloat(personalInfo.costePorPersona) || 0;
                      const bebidasInfo = calcularCosteBebidas();
                      const costeBebidas = bebidasInfo.costePorPersona || 0;
                      const costeTotal = costePlatos + costeMateriales + costePersonal + costeBebidas;
                      const beneficio = formData.precio_venta - costeTotal;
                      const margenPorcentaje = formData.precio_venta > 0 ? ((beneficio / formData.precio_venta) * 100) : 0;
                      
                      return (
                        <div className={`mt-4 rounded-lg p-4 space-y-2 border-2 ${
                          beneficio > 0 ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'
                        }`}>
                          <h5 className="font-bold text-gray-900 mb-2">📊 Análisis de Rentabilidad</h5>
                          
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="bg-white rounded p-2">
                              <div className="text-xs text-gray-500">Coste Total/pers</div>
                              <div className="text-lg font-bold text-red-600">€{Number(costeTotal).toFixed(2)}</div>
                            </div>
                            
                            <div className="bg-white rounded p-2">
                              <div className="text-xs text-gray-500">Precio Venta/pers</div>
                              <div className="text-lg font-bold text-blue-600">€{Number(formData.precio_venta).toFixed(2)}</div>
                            </div>
                            
                            <div className="bg-white rounded p-2">
                              <div className="text-xs text-gray-500">Beneficio/pers</div>
                              <div className={`text-lg font-bold ${beneficio > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                €{Number(beneficio).toFixed(2)}
                              </div>
                            </div>
                            
                            <div className="bg-white rounded p-2">
                              <div className="text-xs text-gray-500">Margen</div>
                              <div className={`text-lg font-bold ${margenPorcentaje > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {Number(margenPorcentaje).toFixed(1)}%
                              </div>
                            </div>
                          </div>
                          
                          <div className="mt-2 pt-2 border-t border-gray-300 text-xs text-gray-600">
                            <div className="flex justify-between">
                              <span>Para 40 personas:</span>
                              <span className={`font-bold ${beneficio > 0 ? 'text-green-700' : 'text-red-700'}`}>
                                Beneficio total: €{Number(beneficio * 40).toFixed(2)}
                              </span>
                            </div>
                          </div>
                          
                          {beneficio < 0 && (
                            <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded text-xs text-red-800">
                              ⚠️ <strong>Atención:</strong> El precio de venta es inferior al coste. Estás perdiendo €{Math.abs(beneficio).toFixed(2)} por persona.
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Nota Informativa */}
                  <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                    <p className="text-sm text-yellow-900 font-semibold mb-2">💡 Información Importante:</p>
                    <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
                      <li>Los costes de personal se calcularán según el evento real y tarifas configuradas</li>
                      <li>Los costes de transporte se calcularán con Google Maps API (próximamente)</li>
                      <li>Este desglose es orientativo para 40 personas, los valores exactos se calcularán en cada presupuesto</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                disabled={saving}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                  saving 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-duetto-primary hover:bg-opacity-90'
                } text-white`}
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {saving ? 'Guardando...' : (editingMenu ? 'Guardar Cambios' : 'Crear Menú')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Crear Material Completo */}
      {showCreateMaterialModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="px-6 py-4 border-b flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-xl font-bold text-gray-900">➕ Nuevo Material</h3>
              <button
                onClick={() => setShowCreateMaterialModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Form */}
            <div className="p-6 space-y-4">
              {/* Nombre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del Material *
                </label>
                <input
                  type="text"
                  value={nuevoMaterialFormData.nombre}
                  onChange={(e) => setNuevoMaterialFormData({...nuevoMaterialFormData, nombre: e.target.value})}
                  placeholder="Ej: Platos desechables 23cm"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                  autoFocus
                />
              </div>

              {/* Categoría y Tipo de Coste */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Categoría *
                  </label>
                  <select
                    value={nuevoMaterialFormData.categoria}
                    onChange={(e) => setNuevoMaterialFormData({...nuevoMaterialFormData, categoria: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                  >
                    <option value="menaje">Menaje</option>
                    <option value="cocina">Cocina</option>
                    <option value="desechables">Desechables</option>
                    <option value="decoracion">Decoración</option>
                    <option value="limpieza">Limpieza</option>
                    <option value="textil">Textil</option>
                    <option value="otros">Otros</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Coste *
                  </label>
                  <select
                    value={nuevoMaterialFormData.tipo_coste}
                    onChange={(e) => setNuevoMaterialFormData({...nuevoMaterialFormData, tipo_coste: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                  >
                    <option value="inventariable">Inventariable (se reutiliza)</option>
                    <option value="consumible">Consumible (se usa y descarta)</option>
                  </select>
                </div>
              </div>

              {/* Campos específicos para Consumible */}
              {nuevoMaterialFormData.tipo_coste === 'consumible' && (
                <div className="bg-blue-50 rounded-lg p-4 space-y-4">
                  <h4 className="font-medium text-blue-800">Configuración de Consumible</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Formato
                      </label>
                      <select
                        value={nuevoMaterialFormData.formato_unidad}
                        onChange={(e) => setNuevoMaterialFormData({...nuevoMaterialFormData, formato_unidad: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="unidades">Unidades</option>
                        <option value="paquete">Paquete</option>
                        <option value="caja">Caja</option>
                        <option value="rollo">Rollo</option>
                        <option value="litros">Litros</option>
                        <option value="kg">Kilogramos</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Uds/Envase
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={nuevoMaterialFormData.cantidad_por_envase}
                        onChange={(e) => handleEnvaseChange('cantidad_por_envase', e.target.value === '' ? '' : (parseInt(e.target.value) || 1))}
                        onBlur={(e) => { if (e.target.value === '') handleEnvaseChange('cantidad_por_envase', 1); }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        €/Envase
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={nuevoMaterialFormData.precio_por_envase}
                        onChange={(e) => handleEnvaseChange('precio_por_envase', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                  {nuevoMaterialFormData.cantidad_por_envase > 0 && nuevoMaterialFormData.precio_por_envase > 0 && (
                    <p className="text-sm text-blue-700">
                      💡 Precio por unidad: <span className="font-bold">{(nuevoMaterialFormData.precio_por_envase / nuevoMaterialFormData.cantidad_por_envase).toFixed(4)}€</span>
                    </p>
                  )}
                </div>
              )}

              {/* Valor de Compra para Inventariable */}
              {nuevoMaterialFormData.tipo_coste === 'inventariable' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor de Compra (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={nuevoMaterialFormData.precio_compra}
                    onChange={(e) => setNuevoMaterialFormData({...nuevoMaterialFormData, precio_compra: parseFloat(e.target.value) || 0})}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                  />
                </div>
              )}

              {/* Stock */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stock Actual
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={nuevoMaterialFormData.stock}
                    onChange={(e) => setNuevoMaterialFormData({...nuevoMaterialFormData, stock: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stock Mínimo (alerta)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={nuevoMaterialFormData.stock_minimo}
                    onChange={(e) => setNuevoMaterialFormData({...nuevoMaterialFormData, stock_minimo: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                  />
                </div>
              </div>

              {/* Ubicación */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ubicación
                </label>
                <div className="flex gap-2">
                  <select
                    value={nuevoMaterialFormData.ubicacion}
                    onChange={(e) => setNuevoMaterialFormData({...nuevoMaterialFormData, ubicacion: e.target.value})}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                  >
                    <option value="">-- Seleccionar ubicación --</option>
                    {ubicaciones.map(u => (
                      <option key={u.id || u.nombre} value={u.nombre}>{u.nombre}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowUbicacionModal(true)}
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    + Nueva
                  </button>
                </div>
              </div>

              {/* Proveedor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Proveedor
                </label>
                <select
                  value={nuevoMaterialFormData.proveedor_id || ''}
                  onChange={(e) => setNuevoMaterialFormData({...nuevoMaterialFormData, proveedor_id: e.target.value || null})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                >
                  <option value="">-- Sin proveedor --</option>
                  {proveedores.map(p => (
                    <option key={p.id} value={p.id}>{p.nombre}</option>
                  ))}
                </select>
              </div>

              {/* Notas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notas
                </label>
                <textarea
                  value={nuevoMaterialFormData.notas}
                  onChange={(e) => setNuevoMaterialFormData({...nuevoMaterialFormData, notas: e.target.value})}
                  rows="2"
                  placeholder="Notas adicionales sobre el material..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent resize-none"
                />
              </div>

              {/* Activo */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="material-activo"
                  checked={nuevoMaterialFormData.activo}
                  onChange={(e) => setNuevoMaterialFormData({...nuevoMaterialFormData, activo: e.target.checked})}
                  className="mr-2 h-4 w-4 text-duetto-primary focus:ring-duetto-primary border-gray-300 rounded"
                />
                <label htmlFor="material-activo" className="text-sm text-gray-700">
                  Material activo
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t flex justify-end space-x-3 sticky bottom-0 bg-white">
              <button
                onClick={() => setShowCreateMaterialModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveNuevoMaterial}
                className="px-4 py-2 bg-duetto-primary text-white rounded-lg hover:bg-opacity-90"
              >
                Crear Material
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Nueva Ubicación */}
      {showUbicacionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold mb-4">Nueva Ubicación</h3>
            <input
              type="text"
              value={nuevaUbicacion}
              onChange={(e) => setNuevaUbicacion(e.target.value)}
              placeholder="Nombre de la ubicación"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
              autoFocus
            />
            <div className="flex space-x-3">
              <button
                onClick={() => { setShowUbicacionModal(false); setNuevaUbicacion(''); }}
                className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateUbicacion}
                className="flex-1 bg-duetto-primary text-white py-2 rounded-lg hover:bg-opacity-90"
              >
                Crear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Plantillas */}
      {showPlantillaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingPlantilla ? 'Editar Plantilla' : 'Nueva Plantilla'}
              </h2>
              <button onClick={() => setShowPlantillaModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de la Plantilla *</label>
                    <input
                      type="text"
                      value={plantillaFormData.nombre}
                      onChange={(e) => setPlantillaFormData({...plantillaFormData, nombre: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="Ej: Plantilla Cóctel Estándar"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                  <textarea
                    value={plantillaFormData.descripcion}
                    onChange={(e) => setPlantillaFormData({...plantillaFormData, descripcion: e.target.value})}
                    rows="3"
                    className="w-full border rounded-lg px-3 py-2 resize-none"
                    placeholder="Descripción de la plantilla y cuándo usarla..."
                  />
                </div>

                {/* Material Fijo */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">Material Fijo (por evento)</h3>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={reloadMateriales}
                        className="text-sm text-gray-600 hover:text-gray-800 px-2 py-1 border rounded hover:bg-gray-50 flex items-center gap-1"
                        title="Actualizar lista de materiales"
                      >
                        <RefreshCw className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => addMaterialFijo(true)}
                        className="text-sm bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700"
                      >
                        + Añadir
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">Material que se necesita independientemente del número de personas</p>
                  
                  {plantillaFormData.material_fijo.map((item, index) => (
                    <MaterialSelectRow
                      key={index}
                      item={item}
                      materiales={materiales}
                      onSelect={(nombre) => {
                        const updated = [...plantillaFormData.material_fijo];
                        updated[index].nombre = nombre;
                        setPlantillaFormData({...plantillaFormData, material_fijo: updated});
                      }}
                      onCreateNew={() => setShowCreateMaterialModal(true)}
                      onDelete={() => {
                        const updated = plantillaFormData.material_fijo.filter((_, i) => i !== index);
                        setPlantillaFormData({...plantillaFormData, material_fijo: updated});
                      }}
                      cantidadField="cantidad"
                      cantidadValue={item.cantidad || 1}
                      onCantidadChange={(val) => {
                        const updated = [...plantillaFormData.material_fijo];
                        updated[index].cantidad = val === '' ? '' : (parseInt(val) || 1);
                        setPlantillaFormData({...plantillaFormData, material_fijo: updated});
                      }}
                      onCantidadBlur={() => {
                        const updated = [...plantillaFormData.material_fijo];
                        if (!updated[index].cantidad) updated[index].cantidad = 1;
                        setPlantillaFormData({...plantillaFormData, material_fijo: updated});
                      }}
                      cantidadLabel="uds/evento"
                    />
                  ))}
                </div>

                {/* Material por Persona */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">Material por Nº de Personas</h3>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={reloadMateriales}
                        className="text-sm text-gray-600 hover:text-gray-800 px-2 py-1 border rounded hover:bg-gray-50 flex items-center gap-1"
                        title="Actualizar lista de materiales"
                      >
                        <RefreshCw className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => addMaterialPorPersona(true)}
                        className="text-sm bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700"
                      >
                        + Añadir
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">Ej: 1 bandeja cada 20 personas</p>
                  
                  {plantillaFormData.material_por_persona.map((item, index) => (
                    <MaterialSelectRowPersona
                      key={index}
                      item={item}
                      materiales={materiales}
                      onSelect={(nombre) => {
                        const updated = [...plantillaFormData.material_por_persona];
                        updated[index].nombre = nombre;
                        setPlantillaFormData({...plantillaFormData, material_por_persona: updated});
                      }}
                      onCreateNew={() => setShowCreateMaterialModal(true)}
                      onDelete={() => {
                        const updated = plantillaFormData.material_por_persona.filter((_, i) => i !== index);
                        setPlantillaFormData({...plantillaFormData, material_por_persona: updated});
                      }}
                      cantidadValue={item.cantidad_unidades || 1}
                      onCantidadChange={(val) => {
                        const updated = [...plantillaFormData.material_por_persona];
                        updated[index].cantidad_unidades = val === '' ? '' : (parseInt(val) || 1);
                        setPlantillaFormData({...plantillaFormData, material_por_persona: updated});
                      }}
                      onCantidadBlur={() => {
                        const updated = [...plantillaFormData.material_por_persona];
                        if (!updated[index].cantidad_unidades) updated[index].cantidad_unidades = 1;
                        setPlantillaFormData({...plantillaFormData, material_por_persona: updated});
                      }}
                      personasValue={item.personas_por_unidad || 1}
                      onPersonasChange={(val) => {
                        const updated = [...plantillaFormData.material_por_persona];
                        updated[index].personas_por_unidad = val === '' ? '' : (parseInt(val) || 1);
                        setPlantillaFormData({...plantillaFormData, material_por_persona: updated});
                      }}
                      onPersonasBlur={() => {
                        const updated = [...plantillaFormData.material_por_persona];
                        if (!updated[index].personas_por_unidad) updated[index].personas_por_unidad = 1;
                        setPlantillaFormData({...plantillaFormData, material_por_persona: updated});
                      }}
                    />
                  ))}
                </div>

                {/* SERVICIOS - Platos tipo servicio */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">🛎️ Servicios</h3>
                    <button
                      type="button"
                      onClick={() => {
                        setPlantillaFormData({
                          ...plantillaFormData,
                          servicios: [...plantillaFormData.servicios, { plato_id: '', nombre: '', cantidad: 1 }]
                        });
                      }}
                      className="text-sm bg-amber-600 text-white px-3 py-1 rounded hover:bg-amber-700"
                    >
                      + Añadir Servicio
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">Platos de tipo servicio que se incluirán en la hoja de material (no en orden de cocina)</p>
                  
                  {plantillaFormData.servicios.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 mb-2 p-2 bg-amber-50 rounded border border-amber-200">
                      <select
                        value={item.plato_id || ''}
                        onChange={(e) => {
                          const updated = [...plantillaFormData.servicios];
                          const selectedPlato = platos.find(p => p.id === e.target.value);
                          updated[index] = {
                            ...updated[index],
                            plato_id: e.target.value,
                            nombre: selectedPlato?.nombre || ''
                          };
                          setPlantillaFormData({...plantillaFormData, servicios: updated});
                        }}
                        className="flex-1 border rounded px-2 py-1"
                      >
                        <option value="">Seleccionar servicio...</option>
                        {platos.filter(p => p.tipo === 'servicio').map(plato => (
                          <option key={plato.id} value={plato.id}>{plato.nombre}</option>
                        ))}
                      </select>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min="1"
                          value={item.cantidad || ''}
                          onChange={(e) => {
                            const updated = [...plantillaFormData.servicios];
                            updated[index].cantidad = e.target.value === '' ? '' : (parseInt(e.target.value) || 1);
                            setPlantillaFormData({...plantillaFormData, servicios: updated});
                          }}
                          onBlur={(e) => {
                            if (e.target.value === '') {
                              const updated = [...plantillaFormData.servicios];
                              updated[index].cantidad = 1;
                              setPlantillaFormData({...plantillaFormData, servicios: updated});
                            }
                          }}
                          className="w-16 border rounded px-2 py-1 text-center"
                        />
                        <span className="text-xs text-gray-500">uds/pax</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const updated = plantillaFormData.servicios.filter((_, i) => i !== index);
                          setPlantillaFormData({...plantillaFormData, servicios: updated});
                        }}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  
                  {platos.filter(p => p.tipo === 'servicio').length === 0 && (
                    <p className="text-sm text-amber-600 italic">No hay platos marcados como servicio. Puedes marcar platos como servicio desde la página de Platos.</p>
                  )}
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={plantillaFormData.activo}
                    onChange={(e) => setPlantillaFormData({...plantillaFormData, activo: e.target.checked})}
                    className="mr-2"
                  />
                  <label className="text-sm text-gray-700">Plantilla activa</label>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t flex justify-end space-x-3">
              <button
                onClick={() => setShowPlantillaModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSavePlantilla}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                {editingPlantilla ? 'Guardar Cambios' : 'Crear Plantilla'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ========== MODAL CATÁLOGO PDF ========== */}
      {showCatalogoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="catalogo-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-5 rounded-t-2xl flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold">Exportar Catálogo de Menús</h2>
                <p className="text-green-100 text-sm">Configura qué incluir en el PDF</p>
              </div>
              <button onClick={() => setShowCatalogoModal(false)} className="p-1 hover:bg-white/20 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Subtítulo */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Subtítulo del catálogo</label>
                <input
                  type="text"
                  value={catalogoConfig.subtitulo}
                  onChange={e => setCatalogoConfig({...catalogoConfig, subtitulo: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Ej: Servicios de Catering, Propuesta para Agencia XYZ..."
                  data-testid="catalogo-subtitulo"
                />
              </div>

              {/* Selección de menús */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Menús a incluir 
                  <span className="text-gray-400 font-normal ml-1">
                    ({catalogoConfig.menu_ids.length === 0 ? 'Todos los activos' : `${catalogoConfig.menu_ids.length} seleccionados`})
                  </span>
                </label>
                <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                  {menus.filter(m => m.tipo === 'menu').map(m => (
                    <label key={m.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm border-b border-gray-50">
                      <input
                        type="checkbox"
                        checked={catalogoConfig.menu_ids.length === 0 || catalogoConfig.menu_ids.includes(m.id)}
                        onChange={e => {
                          if (catalogoConfig.menu_ids.length === 0) {
                            // Primer click: seleccionar solo este
                            setCatalogoConfig({...catalogoConfig, menu_ids: [m.id]});
                          } else if (e.target.checked) {
                            setCatalogoConfig({...catalogoConfig, menu_ids: [...catalogoConfig.menu_ids, m.id]});
                          } else {
                            const newIds = catalogoConfig.menu_ids.filter(id => id !== m.id);
                            setCatalogoConfig({...catalogoConfig, menu_ids: newIds.length === 0 ? [] : newIds});
                          }
                        }}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        data-testid={`catalogo-menu-check-${m.id}`}
                      />
                      <span className="flex-1">{m.nombre}</span>
                      <span className="text-gray-400 text-xs">{m.precio_persona ? `${m.precio_persona}€` : ''}</span>
                    </label>
                  ))}
                </div>
                <button
                  onClick={() => setCatalogoConfig({...catalogoConfig, menu_ids: []})}
                  className="text-xs text-green-600 hover:underline mt-1"
                >
                  Seleccionar todos
                </button>
              </div>

              {/* Servicios */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-gray-700">Servicios</label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!catalogoConfig.excluir_servicios}
                      onChange={e => setCatalogoConfig({...catalogoConfig, excluir_servicios: !e.target.checked})}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      data-testid="catalogo-incluir-servicios"
                    />
                    Incluir todos los servicios
                  </label>
                </div>
                {catalogoConfig.excluir_servicios && (
                  <div className="border border-gray-200 rounded-lg max-h-36 overflow-y-auto bg-gray-50">
                    <p className="px-3 py-1.5 text-xs text-gray-500">Selecciona los servicios que SÍ quieres incluir:</p>
                    {menus.filter(m => m.tipo === 'servicio').map(m => (
                      <label key={m.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-white cursor-pointer text-sm border-b border-gray-100">
                        <input
                          type="checkbox"
                          checked={catalogoConfig.servicios_incluir.includes(m.id)}
                          onChange={e => {
                            if (e.target.checked) {
                              setCatalogoConfig({...catalogoConfig, servicios_incluir: [...catalogoConfig.servicios_incluir, m.id]});
                            } else {
                              setCatalogoConfig({...catalogoConfig, servicios_incluir: catalogoConfig.servicios_incluir.filter(id => id !== m.id)});
                            }
                          }}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="flex-1">{m.nombre}</span>
                        <span className="text-gray-400 text-xs">{m.precio_persona ? `${m.precio_persona}€` : ''}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Opciones de visualización */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-700">Mostrar en el catálogo</h4>
                  
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={catalogoConfig.mostrar_precio}
                      onChange={e => setCatalogoConfig({...catalogoConfig, mostrar_precio: e.target.checked})}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      data-testid="catalogo-mostrar-precio"
                    />
                    <DollarSign className="w-4 h-4 text-gray-400" />
                    Precio por persona (sin IVA)
                  </label>
                  
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={catalogoConfig.mostrar_alergenos}
                      onChange={e => setCatalogoConfig({...catalogoConfig, mostrar_alergenos: e.target.checked})}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      data-testid="catalogo-mostrar-alergenos"
                    />
                    <FileText className="w-4 h-4 text-gray-400" />
                    Alérgenos de los platos
                  </label>
                  
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={catalogoConfig.mostrar_tiempos}
                      onChange={e => setCatalogoConfig({...catalogoConfig, mostrar_tiempos: e.target.checked})}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      data-testid="catalogo-mostrar-tiempos"
                    />
                    <Clock className="w-4 h-4 text-gray-400" />
                    Tiempos (montaje, duración, desmontaje)
                  </label>
                  
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={catalogoConfig.mostrar_personal}
                      onChange={e => setCatalogoConfig({...catalogoConfig, mostrar_personal: e.target.checked})}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      data-testid="catalogo-mostrar-personal"
                    />
                    <Users className="w-4 h-4 text-gray-400" />
                    Personal incluido (ratio por personas)
                  </label>
                </div>

                {/* Modo alérgenos */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-700">Formato de alérgenos</h4>
                  
                  {['iconos', 'texto', 'ambos'].map(modo => (
                    <label key={modo} className={`flex items-center gap-2 text-sm cursor-pointer p-2 rounded-lg border transition-all ${
                      catalogoConfig.modo_alergenos === modo 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    } ${!catalogoConfig.mostrar_alergenos ? 'opacity-40 pointer-events-none' : ''}`}>
                      <input
                        type="radio"
                        name="modo_alergenos"
                        checked={catalogoConfig.modo_alergenos === modo}
                        onChange={() => setCatalogoConfig({...catalogoConfig, modo_alergenos: modo})}
                        className="text-green-600 focus:ring-green-500"
                        data-testid={`catalogo-alergenos-${modo}`}
                      />
                      <span className="capitalize font-medium">
                        {modo === 'iconos' ? 'Solo iconos oficiales' : modo === 'texto' ? 'Solo texto' : 'Iconos + texto'}
                      </span>
                    </label>
                  ))}
                  
                  {catalogoConfig.mostrar_alergenos && (
                    <p className="text-xs text-gray-400 italic">
                      {catalogoConfig.modo_alergenos === 'iconos' 
                        ? 'Iconos oficiales junto a cada plato con leyenda al final'
                        : catalogoConfig.modo_alergenos === 'texto'
                          ? 'Nombre del alérgeno en etiqueta de texto'
                          : 'Ambos formatos para máxima claridad'}
                    </p>
                  )}
                </div>
              </div>

              {/* Preview info */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Eye className="w-4 h-4" />
                  <span>
                    El catálogo incluirá <strong>{catalogoConfig.menu_ids.length === 0 ? menus.filter(m => m.tipo === 'menu' && m.activo).length : catalogoConfig.menu_ids.length}</strong> menús
                    {!catalogoConfig.excluir_servicios && ` + ${menus.filter(m => m.tipo === 'servicio').length} servicios`}
                    {catalogoConfig.excluir_servicios && catalogoConfig.servicios_incluir.length > 0 && ` + ${catalogoConfig.servicios_incluir.length} servicios seleccionados`}
                    . Precios sin IVA con leyenda en cada página.
                  </span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-5 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowCatalogoModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={generarCatalogoPdf}
                disabled={generandoPdf}
                className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
                data-testid="catalogo-generar-btn"
              >
                {generandoPdf ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Generar PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Menus;
